import {
  logEvent,
  getExperiment as statsigGetExperiment,
} from './util.statsig.js';

export const PURCHASE_INTENT_STORAGE_KEY = 'jjb_membership_purchase_intent';
export const CHECKOUT_ID_STORAGE_KEY = 'jjb_membership_embedded_checkout_id';
export const MEMBERSHIP_CHECKOUT_UI_EXPERIMENT = 'membership_checkout_ui';
export const MEMBERSHIP_CHECKOUT_UI_PARAM = 'ui_mode';
export const MEMBERSHIP_CHECKOUT_UI_GROUP_IDS = {
  hosted: 78,
  elements: 79,
};
export const MEMBERSHIP_CHECKOUT_UI_UNKNOWN_GROUP_ID = 80;
export const MEMBERSHIP_CHECKOUT_UI_WAIT_MS = 200;
export const CHECKOUT_TERMINAL_STATES = [
  'COMPLETED',
  'APPLIED',
  'REFUNDED',
  'CANCELLED',
  'EXPIRED',
  'FAILED',
];

export function normalizeMembershipCheckoutUiMode(value) {
  return String(value || '').toLowerCase() === 'hosted' ? 'HOSTED' : 'ELEMENTS';
}

export function membershipCheckoutUiGroupValue(value) {
  const group = String(value ?? '').toLowerCase();
  if (group === 'hosted' || group === 'elements') return group;
  return String(value ?? '');
}

// purpose 必须由调用点显式传入当前流程的最新值；
// 隐式读取可变 context 会让绑卡类流程（SETUP/GIFT_RESUME）在
// HOSTED 组误入"不开弹窗"分支，在隐藏层里创建会话后静默返回。
export function usesHostedCheckoutPage(uiMode, purpose) {
  return uiMode === 'HOSTED' && !['SETUP', 'GIFT_RESUME'].includes(purpose);
}

// 是否预开半屏弹层：普通购买 HOSTED 组外跳不预开；赠转付 HOSTED 组
// 创建在后台进行、拿到收银台 url 直接整页跳转，预开只会闪现 Loading 壳。
// 响应落在半屏/确认/成功分支时由调用点惰性补开（openLayer）。
export function opensEmbeddedCheckoutView(uiMode, purpose) {
  if (purpose === 'GIFT_RESUME') return uiMode !== 'HOSTED';
  return !usesHostedCheckoutPage(uiMode, purpose);
}

export function normalizeEntryPage(value) {
  return value || 'direct';
}

export function normalizeMembershipState(state) {
  const value = state && typeof state === 'object' ? state : {};
  return {
    plan: String(value.plan || 'BASIC').toUpperCase(),
    status: String(
      value.membershipStatus || value.status || 'BASIC',
    ).toUpperCase(),
    billingStatus: String(value.billingStatus || '').toUpperCase(),
    membershipSource: value.membershipSource || 'PAID',
    giftId: value.giftId || null,
    giftEndsAt: value.giftEndsAt || null,
    resumeVisible: value.resumeVisible,
    renewalStatus: value.renewalStatus || null,
    paidThrough: value.paidThrough || value.currentPeriodEndsAt || '',
    billingCycle: value.billingCycle || value.billing_cycle || '',
  };
}

export function requiresPaymentMethodUpdate(state) {
  const membership = normalizeMembershipState(state);
  return (
    membership.status === 'DUNNING' ||
    (membership.billingStatus === 'UNPAID' &&
      ['ACTIVE', 'CANCELLING'].includes(membership.status))
  );
}

export function isActivePaidMembership(state, now = Date.now()) {
  const membership = normalizeMembershipState(state);
  if (membership.membershipSource === 'GIFT') return false;
  if (membership.plan !== 'PRO' && membership.plan !== 'PREMIUM') return false;
  if (membership.status === 'ACTIVE') return true;
  if (
    membership.status !== 'CANCELLING' &&
    !requiresPaymentMethodUpdate(membership)
  )
    return false;
  const paidThrough = Date.parse(membership.paidThrough);
  return Number.isFinite(paidThrough) && paidThrough > now;
}

export function getGenerationTaskCount(data) {
  if (data?.allTotalTasksNum === null || data?.allTotalTasksNum === undefined) {
    return null;
  }
  const count = Number(data?.allTotalTasksNum);
  return Number.isFinite(count) && count >= 0 ? count : null;
}

export function hasRequiredGenerationCount(data) {
  return (getGenerationTaskCount(data) ?? 0) > 10;
}

export function createPurchaseIntent({
  entryPage,
  returnUrl,
  now = Date.now(),
}) {
  const safeReturnUrl = String(returnUrl || '/');
  return {
    version: 1,
    entry_page: normalizeEntryPage(entryPage),
    plan_type: 'pro',
    billing_cycle: 'monthly',
    return_url:
      safeReturnUrl.startsWith('/') && !safeReturnUrl.startsWith('//')
        ? safeReturnUrl
        : '/',
    created_at: now,
    expires_at: now + 10 * 60 * 1000,
  };
}

export function parsePurchaseIntent(raw, { now = Date.now() } = {}) {
  let value;
  try {
    value = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
  if (
    !value ||
    value.version !== 1 ||
    value.expires_at <= now ||
    value.created_at > now
  )
    return null;
  if (value.plan_type !== 'pro' || value.billing_cycle !== 'monthly')
    return null;
  if (
    typeof value.return_url !== 'string' ||
    !value.return_url.startsWith('/') ||
    value.return_url.startsWith('//')
  ) {
    return null;
  }
  return { ...value, entry_page: normalizeEntryPage(value.entry_page) };
}

export function resolveCheckoutState(status) {
  const value = String(status || '').toUpperCase();
  if (value === 'COMPLETED' || value === 'APPLIED') return 'payment_success';
  if (CHECKOUT_TERMINAL_STATES.includes(value)) return 'payment_failed';
  return 'confirming';
}

function checkoutFailureMessage(status, fallback) {
  return String(status || '').toUpperCase() === 'REFUNDED'
    ? 'Your payment was refunded.'
    : fallback;
}

export function createIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `membership-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function isMembershipPopupBackdropClick(target, currentTarget) {
  return target === currentTarget;
}

const EXPERIMENT_NAME = 'promotion_popup';
const HIDE_GIFT_BOX_EXPERIMENT_NAME = 'hide_gift_box';
const PROMOTION_POPUP_GROUP_IDS = { old: 31, new: 32, none: 33 };
const HIDE_GIFT_BOX_GROUP_IDS = { false: 35, true: 36 };
const STORAGE_KEY_PREFIX = 'newsletter_popup_';
const MEMBERSHIP_EXPERIMENT_NAME = 'show_vip_banner';
const MEMBERSHIP_SDK_FALLBACK_URL =
  'https://static.airwallex.com/components/sdk/v1/index.js';
const CHECKOUT_CACHE_TTL_MS = 5 * 60 * 1000;
const PAYMENT_FORM_RETAIN_MS = 60_000;
const CHECKOUT_PREFETCH_MS = 60_000;
const PREPARE_TRUST_MS = 3_000;
const CHECKOUT_SOURCE_STORAGE_KEY_PREFIX = 'jjb_membership_checkout_source_';

function isTestMode() {
  return window.__RELEASE__.isTest;
}

function getExperiment(experimentName) {
  if (isTestMode() && window.jjbUtils?.overrideExperiment) {
    return window.jjbUtils.overrideExperiment(experimentName);
  }
  return statsigGetExperiment(experimentName);
}

function getMembershipApiPrefix() {
  return `${isTestMode() ? '/apps/ai-test' : '/apps/ai'}/membership`;
}

function getApiPrefix() {
  return isTestMode() ? '/apps/ai-test' : '/apps/ai';
}

async function requestMembership(path, options = {}) {
  const response = await fetch(getMembershipApiPrefix() + path, {
    method: options.method || 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });
  options.onResponse?.({ traceId: response.headers.get('X-Trace-Id') });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload || payload.success === false) {
    const error = new Error(
      payload?.message || payload?.errorMessage || 'Membership request failed.',
    );
    error.code = payload?.code;
    error.status = response.status;
    throw error;
  }
  return payload.data;
}

async function hasRequiredGenerationCountForAutoOpen() {
  let identity = {};
  try {
    identity = window.jjbUtils?.getIdentity?.() || {};
  } catch {}
  const params = new URLSearchParams({ activityType: 'canvas', pageSize: '1' });
  Object.entries(identity).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  try {
    const response = await fetch(
      `${getApiPrefix()}/activity/tasks?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      },
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || payload.success === false) return false;
    return hasRequiredGenerationCount(payload.data);
  } catch {
    return false;
  }
}

async function mergeAnonymousActivityAfterLogin() {
  let identity = {};
  try {
    identity = window.jjbUtils?.getIdentity?.() || {};
  } catch {}
  const customerId = identity.customerId ? String(identity.customerId) : '';
  const anonymousId = identity.anonymousId ? String(identity.anonymousId) : '';
  if (!customerId || !anonymousId) return;
  const startedAt = performance.now();
  let outcome = 'success';
  try {
    // 与 Checkout 一致的超时兜底：请求永久 pending 时不再悬挂埋点收口。
    const response = await checkoutTimeout(
      fetch(`${getApiPrefix()}/activity/tasks/merge`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId, anonymousId }),
      }),
      'activity merge',
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || payload.success === false) {
      throw new Error(
        payload?.message || payload?.errorMessage || 'Activity merge failed.',
      );
    }
  } catch (error) {
    outcome = 'error';
    console.warn('[jjb-membership-popup] login activity merge failed', error);
  } finally {
    try {
      await trackMembership(
        'membership_activity_merge',
        { outcome, duration_ms: Math.round(performance.now() - startedAt) },
        { monitor: true, ga4: false },
      );
      Promise.resolve(window.zlog?.flushTracking?.()).catch(() => {});
    } catch {}
  }
}

function showCheckoutRequestError(error) {
  if (typeof window.toast !== 'function') return;
  if (error?.status === 400 && String(error.code) === '1027') {
    window.toast(
      'Your membership information is syncing. Please refresh the page.',
      10000,
    );
    return;
  }
  if (error?.status === 429) {
    window.toast(
      'Please refresh the page and complete the human verification process.',
      10000,
    );
    return;
  }
  if (error?.status >= 500 && error.status <= 599) {
    window.toast(
      `${error.message || 'Unable to start secure checkout.'} Please take a screenshot and contact customer support.`,
      10000,
    );
  }
}

function trackMembership(eventName, params = {}, options = {}) {
  return window?.zlog?.track?.(
    eventName,
    { page: window.location.pathname, ...params },
    { statsig: false, monitor: false, ...options },
  );
}

function getAirwallexPaymentUrl(data) {
  return data?.url || '';
}

function getAirwallexErrorParams(error, checkoutId) {
  return {
    checkout_id: checkoutId || undefined,
    error_code: error?.code || error?.errorCode || undefined,
    error_message: error?.message || String(error || 'Unknown Airwallex error'),
  };
}

function getThemeMembershipState() {
  return (
    window.theme?.membershipState ||
    window.JJB_CUSTOMER_MEMBERSHIP_STATE ||
    null
  );
}

function isLoggedIn() {
  return Boolean(window.theme?.customerId);
}

function trackCheckoutMonitor(eventName, params = {}) {
  return trackMembership(
    eventName,
    { customerId: window.theme?.customerId || null, ...params },
    { monitor: true },
  );
}

function getSafeReturnUrl() {
  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

function createCheckoutSourceHash() {
  if (typeof window.crypto?.randomUUID === 'function') {
    return `h_${window.crypto.randomUUID()}`;
  }
  return `h_${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

function storeCheckoutSource(source) {
  const hash = createCheckoutSourceHash();
  try {
    window.localStorage.setItem(
      CHECKOUT_SOURCE_STORAGE_KEY_PREFIX + hash,
      source,
    );
  } catch {}
  return hash;
}

function getLoginUrl(returnUrl) {
  return `/customer_authentication/login?return_to=${encodeURIComponent(returnUrl)}`;
}

function getCheckoutSuccessUrl(planType = 'pro') {
  const url = new URL(
    '/pages/checkout-succeed',
    'https://exn11x-id.myshopify.com',
  );
  url.searchParams.set('source', storeCheckoutSource(getSafeReturnUrl()));
  url.searchParams.set('plan', planType === 'premium' ? 'PREMIUM' : 'PRO');
  return url.href;
}

function checkoutTimeout(promise, phase, timeoutMs) {
  // e2e 可通过 window.jjbMembershipRequestTimeoutMs 缩短超时窗口；生产默认 20s。
  const timeout =
    timeoutMs ??
    ((typeof window !== 'undefined' &&
      Number(window.jjbMembershipRequestTimeoutMs)) ||
      20_000);
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = window.setTimeout(
        () =>
          reject(new Error(`Payment ${phase} timed out. Please try again.`)),
        timeout,
      );
    }),
  ]).finally(() => window.clearTimeout(timer));
}

let airwallexSdkPromise;
function loadAirwallexSdk(url) {
  const sdk = window.AirwallexComponentsSDK || window.Airwallex;
  if (sdk) return Promise.resolve(sdk);
  if (airwallexSdkPromise) return airwallexSdkPromise;
  const script = document.createElement('script');
  airwallexSdkPromise = checkoutTimeout(
    new Promise((resolve, reject) => {
      script.src = url || MEMBERSHIP_SDK_FALLBACK_URL;
      script.async = true;
      script.onload = () => {
        const loaded = window.AirwallexComponentsSDK || window.Airwallex;
        loaded
          ? resolve(loaded)
          : reject(new Error('Airwallex SDK unavailable.'));
      };
      script.onerror = () =>
        reject(new Error('Unable to load the payment form.'));
      document.head.appendChild(script);
    }),
    'SDK load',
  ).catch((error) => {
    script.remove();
    airwallexSdkPromise = undefined;
    throw error;
  });
  return airwallexSdkPromise;
}

const billingPromises = new Map();
function initializeAirwallex(sdk, environment) {
  const locale = document.documentElement.lang || 'en';
  const key = `${environment}:${locale}`;
  if (!billingPromises.has(key)) {
    billingPromises.set(
      key,
      checkoutTimeout(
        Promise.resolve().then(() =>
          sdk.init({
            env: environment,
            locale,
            enabledElements: ['payments', 'billing'],
          }),
        ),
        'SDK initialization',
      ).catch((error) => {
        billingPromises.delete(key);
        throw error;
      }),
    );
  }
  return billingPromises.get(key);
}

function prewarmAirwallex() {
  return loadAirwallexSdk()
    .then((sdk) => initializeAirwallex(sdk, isTestMode() ? 'demo' : 'prod'))
    .catch(() => {});
}

function createCheckoutAttempt(uiMode, source, startedAt) {
  return (
    window.jjbCheckoutRuntime?.createAttempt?.(uiMode, source, startedAt) || {
      bind() {},
      record() {},
      finish() {},
      async measure(_phase, operation) {
        return operation();
      },
    }
  );
}

function createMembershipPopupController() {
  const root = document.querySelector('[data-jjb-membership-popup]');
  if (!root) return null;
  const offer = root.querySelector('[data-jjb-membership-offer]');
  const checkout = root.querySelector('[data-jjb-membership-checkout]');
  const resultDialog = root.querySelector('[data-jjb-membership-result]');
  const mount = root.querySelector('[data-jjb-membership-checkout-mount]');
  const paymentFormHost = root.querySelector(
    '[data-jjb-membership-payment-form-host]',
  );
  const checkoutSubtotal = root.querySelector(
    '[data-jjb-membership-checkout-subtotal]',
  );
  const checkoutSubtotalAmount = root.querySelector(
    '[data-jjb-membership-checkout-subtotal-amount]',
  );
  const checkoutAdjustment = root.querySelector(
    '[data-jjb-membership-checkout-adjustment]',
  );
  const checkoutAdjustmentAmount = root.querySelector(
    '[data-jjb-membership-checkout-adjustment-amount]',
  );
  const checkoutLoading = root.querySelector(
    '[data-jjb-membership-checkout-loading]',
  );
  const status = root.querySelector('[data-jjb-membership-checkout-status]');
  const retry = root.querySelector('[data-jjb-membership-checkout-retry]');
  const checkoutClose = root.querySelector(
    '[data-jjb-membership-checkout-close]',
  );
  const planLabel = root.querySelector('[data-jjb-membership-checkout-plan]');
  const checkoutTitle = root.querySelector('#jjb-membership-checkout-title');
  const priceLabel = root.querySelector('[data-jjb-membership-checkout-price]');
  const billingLabel = root.querySelector(
    '.jjb-membership-checkout__billing-label',
  );
  const resultMessage = root.querySelector(
    '[data-jjb-membership-result-message]',
  );
  const resultRetry = root.querySelector('[data-jjb-membership-result-retry]');
  const resultClose = root.querySelector('[data-jjb-membership-result-close]');
  let context = null;
  let latestSdkError = null;
  let latestPaymentError = null;
  let sdkLoadStartedAt = 0;
  let checkoutState = 'closed';
  let checkoutRecovery = null;
  let lastClosedCheckout = null;
  let activeRequest = 0;
  let abortController = null;
  let paymentElement = null;
  let paymentCheckout = null;
  let pendingCheckout = null;
  let checkoutTrust = null;
  const disposedCheckouts = new WeakSet();
  let paymentSession = null;
  let retainedPaymentSession = null;
  let retainedPaymentTimer;
  let idempotencyKey = '';
  let checkoutIntentExpiresAt = 0;
  let checkoutAttempt;
  let readyTimer;
  let endReadyWait;
  let startingCheckout = false;
  let previousFocus = null;
  let hasOpened = false;
  let pollingTimer;
  let pollingStartedAt = 0;
  let successCloseTimer;
  const begunCheckoutIds = new Set();
  const fallbackCheckoutIntents = new Map();
  const designMode = root.dataset.designMode === 'true';
  const popupSectionId = root.dataset.popupSectionId || '';
  const popupDelayDays = getDelayDays(root);
  let hiddenShopifyChat = [];
  let ctaLoading = false;
  let checkoutUiModePromise;
  // 半屏 checkout 视图弹起时的一次性通知；openCheckout 据此在弹窗弹起时
  // 即结束入口按钮 loading，而不是等到支付初始化完成。
  let notifyCheckoutViewShown = null;

  function hideShopifyChat() {
    if (hiddenShopifyChat.length) return;
    hiddenShopifyChat = [...document.querySelectorAll('shopify-chat')].map(
      (element) => {
        const display = element.style.display;
        element.style.display = 'none';
        return { element, display };
      },
    );
  }

  function restoreShopifyChat() {
    hiddenShopifyChat.forEach(({ element, display }) => {
      element.style.display = display;
    });
    hiddenShopifyChat = [];
  }

  function normalizeCheckoutBillingCycle(value) {
    const normalized = String(value || '')
      .trim()
      .toLowerCase();
    return normalized === 'year' || normalized === 'yearly'
      ? 'yearly'
      : 'monthly';
  }

  function checkoutContext(options = {}) {
    return {
      entry_page: normalizeEntryPage(options.entry_page),
      plan_type: String(options.plan_type || 'pro').toLowerCase(),
      billing_cycle: normalizeCheckoutBillingCycle(options.billing_cycle),
      plan_label:
        options.plan_label ||
        (options.plan_type === 'premium'
          ? 'Premium Membership'
          : 'Pro Membership'),
      display_price: String(
        options.display_price || root.dataset.defaultCheckoutPrice || '',
      ),
      action_type: options.action_type || 'purchase',
      purpose: ['SETUP', 'GIFT_RESUME'].includes(options.purpose)
        ? options.purpose
        : 'PURCHASE',
      giftId: options.giftId || null,
      signupId: options.signupId || null,
      scheduledStartsAt: options.scheduledStartsAt || null,
    };
  }

  function isGiftResume() {
    return context?.purpose === 'GIFT_RESUME';
  }
  function isSetupCheckout() {
    return context?.purpose === 'SETUP' || isGiftResume();
  }

  function giftCheckoutData(data) {
    context.scheduledStartsAt = data.scheduledStartsAt;
    context.plan_type = data.targetPlan === 'PREMIUM' ? 'premium' : 'pro';
    context.billing_cycle =
      data.targetBillingCycle === 'YEAR' ? 'yearly' : 'monthly';
    context.plan_label =
      data.targetPlan === 'PREMIUM' ? 'Premium Membership' : 'Pro Membership';
    return {
      ...data,
      id: data.signupId,
      mode: 'GIFT_RESUME',
      clientSecret: data.checkout?.clientSecret || null,
      // HOSTED 会话唯一可跳转凭据；ELEMENTS 会话为 null。
      url: data.checkout?.url || null,
      status: ['SUBSCRIPTION_SCHEDULED', 'ACTIVATED'].includes(data.status)
        ? 'COMPLETED'
        : ['FAILED', 'CANCELLED'].includes(data.status)
          ? 'CANCELLED'
          : data.checkout?.clientSecret
            ? 'ACTIVE'
            : 'PROCESSING',
    };
  }

  async function readCheckout(id, options) {
    const data = await requestMembership(
      isGiftResume()
        ? `/gift/signups/${encodeURIComponent(id)}`
        : `/checkouts/${encodeURIComponent(id)}`,
      options,
    );
    return isGiftResume() ? giftCheckoutData(data) : data;
  }

  function resolveCheckoutUiMode() {
    if (!checkoutUiModePromise) {
      checkoutUiModePromise = new Promise((resolve) => {
        let settled = false;
        const finish = (uiMode, record, raw) => {
          if (settled) return;
          settled = true;
          if (record) {
            recordExperimentGroup(
              MEMBERSHIP_CHECKOUT_UI_EXPERIMENT,
              MEMBERSHIP_CHECKOUT_UI_PARAM,
              membershipCheckoutUiGroupValue(raw),
              MEMBERSHIP_CHECKOUT_UI_GROUP_IDS,
              MEMBERSHIP_CHECKOUT_UI_UNKNOWN_GROUP_ID,
            );
          }
          resolve(uiMode);
        };
        getExperiment(MEMBERSHIP_CHECKOUT_UI_EXPERIMENT)
          .then((experiment) => {
            const raw = experiment?.get?.(
              MEMBERSHIP_CHECKOUT_UI_PARAM,
              'hosted',
            );
            finish(normalizeMembershipCheckoutUiMode(raw), true, raw);
          })
          .catch(() => finish('ELEMENTS', false, 'elements'));
        window.setTimeout(
          () => finish('ELEMENTS', false, 'elements'),
          MEMBERSHIP_CHECKOUT_UI_WAIT_MS,
        );
      });
    }
    return checkoutUiModePromise;
  }

  function applyCheckoutMode(data) {
    // 浏览器缓存只提供恢复线索，服务端会话 mode 才决定换卡或购买语义。
    if (data?.mode === 'GIFT_RESUME') {
      context.purpose = 'GIFT_RESUME';
      context.scheduledStartsAt = data.scheduledStartsAt;
      syncCheckoutHeader();
      return;
    }
    const purpose =
      String(data?.mode || '').toUpperCase() === 'SETUP' ? 'SETUP' : 'PURCHASE';
    context.purpose = purpose;
    if (purpose === 'SETUP') context.action_type = 'payment_method_setup';
    else if (context.action_type === 'payment_method_setup')
      context.action_type = 'purchase';
    syncCheckoutHeader();
  }

  async function refreshCheckoutMembership(signal) {
    const customerId = String(window.theme?.customerId || '');
    const state = await checkoutTimeout(
      requestMembership('/me', { signal }),
      'membership status',
    );
    if (signal?.aborted)
      throw new DOMException('Checkout closed.', 'AbortError');
    if (customerId !== String(window.theme?.customerId || ''))
      throw new Error(
        'Your customer account has changed. Please reopen checkout.',
      );
    if (!state || typeof state !== 'object')
      throw new Error('Unable to check membership status. Please try again.');
    if (window.theme) window.theme.membershipState = state;
    window.JJB_CUSTOMER_MEMBERSHIP_STATE = state;
    return normalizeMembershipState(state);
  }

  function processingMessage() {
    if (isGiftResume()) return 'Confirming your scheduled subscription...';
    return isSetupCheckout()
      ? 'Updating your payment method...'
      : 'Processing your payment...';
  }

  async function completeCheckoutResult(data, restored = false) {
    lastClosedCheckout = null;
    applyCheckoutMode(data);
    retireCheckoutIntent(idempotencyKey);
    checkoutAttempt?.finish('completed');
    setStoredCheckoutId('');
    checkoutState = 'payment_success';
    root.dataset.checkoutState = checkoutState;
    if (isGiftResume()) {
      showResult(
        'success',
        '<strong>Subscription scheduled.</strong> Your subscription starts on ' +
          new Date(context.scheduledStartsAt).toLocaleString() +
          '. Your gift membership stays active until then.',
      );
      window.dispatchEvent(new CustomEvent('jjb:membership-updated'));
      void refreshCheckoutMembership().catch(() => {});
      return;
    }
    if (isSetupCheckout()) {
      showResult(
        'success',
        '<strong>Payment method updated.</strong> Your membership will refresh after the renewal payment is confirmed.',
      );
      window.dispatchEvent(new CustomEvent('jjb:membership-updated'));
      // 换卡完成不等于续费入账，只读取当前权益，不在前端恢复套餐。
      void refreshCheckoutMembership().catch(() => {});
      return;
    }
    showResult(
      'success',
      restored
        ? 'This payment was confirmed. Your current membership details will refresh.'
        : `Congrats! <strong>You're now on ${getResultPlanLabel(data?.planLabel)}!</strong>`,
    );
    scheduleSuccessReload();
    if (!restored) await fireConfetti();
  }

  function preparationScope(value) {
    try {
      return JSON.stringify([
        window.theme?.customerId,
        isTestMode(),
        value.plan_type,
        value.billing_cycle,
        value.entry_page,
        value.action_type,
        value.purpose,
        value.giftId,
        window.location.href,
        document.documentElement.lang,
        window.Shopify?.customerPrivacy?.currentVisitorConsent?.(),
      ]);
    } catch {
      return null;
    }
  }

  function closedCheckoutScope() {
    // purchase/upgrade会随刚完成的付款改变；身份、目标、环境及同意范围仍隔离。
    return preparationScope({ ...context, action_type: 'purchase' });
  }

  function disposeCheckout(value) {
    if (!value || typeof value !== 'object' || disposedCheckouts.has(value))
      return;
    disposedCheckouts.add(value);
    try {
      value.destroy?.();
    } catch {}
  }

  function discardPreparedCheckout(reason = 'closed') {
    const prepared = pendingCheckout;
    if (!prepared) return;
    pendingCheckout = null;
    prepared.discarded = true;
    window.clearTimeout(prepared.timer);
    prepared.abort.abort();
    disposeCheckout(prepared.checkout);
    prepared.checkout = null;
    prepared.data = null;
    clearCheckoutTrust();
    prepared.attempt.record(
      'prefetch_discard',
      performance.now() - prepared.startedAt,
      reason,
    );
    prepared.attempt.finish(reason === 'error' ? 'error' : 'closed');
  }

  function clearCheckoutTrust() {
    checkoutTrust?.abort?.abort();
    checkoutTrust = null;
  }

  function armCheckoutTrust(element, { freeze = false } = {}) {
    if (!element || element.nodeType !== 1) return;
    if (checkoutTrust?.element === element) {
      checkoutTrust.pointerInside = true;
      if (freeze) freezeCheckoutTrust(element);
      return;
    }
    clearCheckoutTrust();
    const abort = new AbortController();
    checkoutTrust = {
      element,
      abort,
      pointerInside: true,
      leftOnce: false,
      visibilityBroken: document.visibilityState !== 'visible',
      frozen: null,
    };
    const signal = abort.signal;
    const markLeft = () => {
      if (!checkoutTrust || checkoutTrust.element !== element) return;
      checkoutTrust.pointerInside = false;
      if (!checkoutTrust.frozen) checkoutTrust.leftOnce = true;
    };
    element.addEventListener('pointerleave', markLeft, { signal });
    element.addEventListener('pointercancel', markLeft, { signal });
    element.addEventListener('blur', markLeft, { signal });
    element.addEventListener(
      'pointerdown',
      () => freezeCheckoutTrust(element),
      { signal },
    );
    element.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.key === ' ')
          freezeCheckoutTrust(element);
      },
      { signal },
    );
    element.addEventListener('click', () => freezeCheckoutTrust(element), {
      signal,
      capture: true,
    });
    if (freeze) freezeCheckoutTrust(element);
  }

  function snapshotCheckoutTrust(element) {
    const prepared = pendingCheckout;
    const denied = { skipPost: false, skipMe: false };
    if (!checkoutTrust || checkoutTrust.element !== element) return denied;
    if (checkoutTrust.leftOnce || checkoutTrust.visibilityBroken) return denied;
    if (document.visibilityState !== 'visible') return denied;
    if (!prepared || prepared.discarded || prepared.failed || !prepared.data)
      return denied;
    if (
      !prepared.succeededAt ||
      Date.now() - prepared.succeededAt > PREPARE_TRUST_MS
    )
      return denied;
    if (
      String(window.theme?.customerId || '') !==
      String(prepared.customerId || '')
    )
      return denied;
    if (getStoredCheckoutId()) return denied;
    const membership = prepared.membership;
    if (!membership || membership.plan !== 'BASIC') return denied;
    if (requiresPaymentMethodUpdate(membership)) return denied;
    if (!checkoutTrust.pointerInside && document.activeElement !== element)
      return denied;
    if (
      prepared.data.status !== 'ACTIVE' ||
      !(prepared.data.clientSecret || prepared.data.client_secret)
    )
      return denied;
    return { skipPost: true, skipMe: true, membership };
  }

  function freezeCheckoutTrust(element) {
    if (!checkoutTrust || checkoutTrust.element !== element) return;
    if (checkoutTrust.frozen) return;
    checkoutTrust.frozen = snapshotCheckoutTrust(element);
  }

  function consumeCheckoutTrust() {
    const frozen = checkoutTrust?.frozen;
    if (checkoutTrust) checkoutTrust.frozen = null;
    return frozen?.skipPost ? frozen : { skipPost: false, skipMe: false };
  }

  function visibleDialog() {
    if (!resultDialog.hidden) return resultDialog;
    return checkout.hidden
      ? root.querySelector('[data-jjb-membership-popup-view]:not([hidden])')
      : checkout;
  }

  function syncView() {
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    root
      .querySelectorAll('[data-jjb-membership-popup-view]')
      .forEach((view) => {
        view.hidden =
          view.dataset.jjbMembershipPopupView === 'mobile' ? !mobile : mobile;
      });
  }

  function lockPage(locked) {
    document.documentElement.style.overflow = locked ? 'hidden' : '';
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  function setCheckoutState(nextState, message) {
    checkoutState = nextState;
    root.dataset.checkoutState = nextState;
    status.innerHTML = message || '';
    checkoutClose.disabled =
      nextState === 'submitting' || nextState === 'confirming';
    retry.hidden =
      nextState !== 'init_error' && nextState !== 'confirm_timeout';
    retry.textContent =
      nextState === 'confirm_timeout' ? 'Check status' : 'Try again';
    checkoutLoading.hidden = nextState !== 'checkout_loading';
    if (nextState !== 'ready') mount.hidden = true;
  }

  function showResult(state, message, retryLabel = '') {
    destroyPaymentElement();
    checkout.hidden = true;
    offer.hidden = true;
    resultDialog.hidden = false;
    resultDialog.dataset.resultState = state;
    resultMessage.innerHTML = typeof message === 'string' ? message : '';
    resultRetry.hidden = !retryLabel;
    resultRetry.textContent = retryLabel;
    resultClose.disabled =
      state === 'processing' && checkoutState !== 'confirm_timeout';
    focusDialog();
  }

  function showRefundedCheckout() {
    setStoredCheckoutId('');
    setCheckoutState('payment_failed', '');
    showResult('failed', 'Your payment was refunded.', 'Retry');
  }

  function formatCheckoutAmount(data) {
    const chargeCents = Number(data?.chargeCents);
    const currency = String(data?.currency || '').toUpperCase();
    if (
      !Number.isInteger(chargeCents) ||
      chargeCents < 0 ||
      !/^[A-Z]{3}$/.test(currency)
    )
      return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(chargeCents / 100);
    } catch {
      return `${currency} ${(chargeCents / 100).toFixed(2)}`;
    }
  }

  function syncCheckoutAmount(data) {
    if (isSetupCheckout()) {
      checkoutSubtotal.hidden = true;
      checkoutAdjustment.hidden = true;
      priceLabel.textContent = '';
      return;
    }
    const formattedAmount = formatCheckoutAmount(data);
    const creditCents = Number(data?.creditCents);
    const planAmountCents =
      Number(data?.chargeCents) + (data?.mode === 'PAYMENT' ? creditCents : 0);
    const formattedPlanAmount =
      Number.isSafeInteger(planAmountCents) &&
      (data?.mode !== 'PAYMENT' ||
        (Number.isSafeInteger(creditCents) && creditCents >= 0))
        ? formatCheckoutAmount({
            chargeCents: planAmountCents,
            currency: data?.currency,
          })
        : '';
    // 报价返回后，套餐金额必须与应付和抵扣一致，不能继续显示静态首年优惠价。
    if (formattedAmount && formattedPlanAmount) {
      priceLabel.textContent = formattedPlanAmount;
    }
    const formattedCredit =
      Number.isInteger(creditCents) && creditCents > 0
        ? formatCheckoutAmount({
            chargeCents: creditCents,
            currency: data?.currency,
          })
        : '';
    checkoutSubtotalAmount.textContent = formattedAmount;
    checkoutSubtotal.hidden = !formattedAmount;
    checkoutAdjustmentAmount.textContent = formattedCredit
      ? `-${formattedCredit}`
      : '';
    checkoutAdjustment.hidden = !formattedCredit;
    checkoutSubtotal.dataset.checkoutMode = String(data?.mode || '');
    checkoutSubtotal.dataset.creditCents = String(data?.creditCents ?? '');
  }

  function getResultPlanLabel(label) {
    const fallback = context.plan_type === 'premium' ? 'Premium' : 'Pro';
    return String(label || fallback).replace(/\s+Membership$/i, '');
  }

  async function waitForConfetti() {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 3000) {
      if (typeof window.confetti === 'function') return window.confetti;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    return null;
  }

  async function fireConfetti() {
    const confetti = await waitForConfetti();
    if (!confetti) return;
    try {
      confetti({
        disableForReducedMotion: true,
        origin: { x: 0.5, y: 0.72 },
        particleCount: 100,
        spread: 90,
        startVelocity: 48,
        zIndex: 10000,
      });
    } catch {}
  }

  function focusDialog() {
    window.requestAnimationFrame(() => {
      const dialog = visibleDialog();
      const focusable = dialog?.querySelector(
        'button:not([disabled]), a[href], input, [tabindex="0"]',
      );
      focusable?.focus({ preventScroll: true });
    });
  }

  function openLayer() {
    // 本页已选择会员窗口后，迟到的自动分流不能再打开会员或营销弹窗。
    hasOpened = true;
    previousFocus = document.activeElement;
    syncView();
    root.hidden = false;
    hideShopifyChat();
    lockPage(true);
    focusDialog();
  }

  function destroyPaymentElement() {
    window.clearTimeout(retainedPaymentTimer);
    retainedPaymentSession = null;
    paymentSession = null;
    window.clearTimeout(readyTimer);
    endReadyWait?.('closed');
    endReadyWait = undefined;
    try {
      paymentElement?.unmount?.();
      paymentElement?.destroy?.();
    } catch {}
    paymentElement = null;
    disposeCheckout(paymentCheckout);
    paymentCheckout = null;
    paymentFormHost.replaceChildren();
  }

  function paymentSessionKey(data) {
    if (!idempotencyKey) return null;
    try {
      return JSON.stringify([
        window.theme?.customerId,
        isTestMode(),
        idempotencyKey,
        context?.plan_type,
        context?.billing_cycle,
        context?.entry_page,
        context?.action_type,
        window.location.href,
        document.documentElement.lang,
        window.Shopify?.customerPrivacy?.currentVisitorConsent?.(),
        data.checkoutId,
        data.clientSecret || data.client_secret,
        data.environment,
        data.sdkUrl,
        data.mode,
        data.currency,
        data.chargeCents,
        data.creditCents,
      ]);
    } catch {
      return null;
    }
  }

  function retainReadyPaymentElement() {
    const session = paymentSession;
    if (
      checkoutState !== 'ready' ||
      !session?.ready ||
      !session.reusable ||
      !session.key ||
      (session.retainUntil && session.retainUntil <= Date.now())
    )
      return false;
    session.binding = null;
    session.retainUntil ||= Date.now() + PAYMENT_FORM_RETAIN_MS;
    retainedPaymentSession = session;
    window.clearTimeout(retainedPaymentTimer);
    retainedPaymentTimer = window.setTimeout(() => {
      if (retainedPaymentSession === session) destroyPaymentElement();
    }, session.retainUntil - Date.now());
    return true;
  }

  async function waitForCheckoutPresentation(requestId) {
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    const animations = checkout.getAnimations();
    if (!animations.length) return;
    await Promise.allSettled(animations.map((animation) => animation.finished));
    if (requestId !== activeRequest || checkout.hidden)
      throw new DOMException('Checkout closed.', 'AbortError');
  }

  function clearPolling() {
    if (pollingTimer) window.clearTimeout(pollingTimer);
    pollingTimer = undefined;
  }

  function clearSuccessCloseTimer() {
    if (successCloseTimer) window.clearTimeout(successCloseTimer);
    successCloseTimer = undefined;
  }

  function scheduleSuccessReload() {
    clearSuccessCloseTimer();
    successCloseTimer = window.setTimeout(() => {
      successCloseTimer = undefined;
      window.location.reload();
    }, 4000);
  }

  function closeLayer() {
    const confirmationTimedOut = checkoutState === 'confirm_timeout';
    const storedCheckout = getStoredCheckout();
    const keepUnconfirmedCheckout =
      storedCheckout?.pendingConfirmation === true ||
      ['SETUP', 'GIFT_RESUME'].includes(storedCheckout?.context?.purpose);
    if (
      checkoutState === 'submitting' ||
      checkoutState === 'confirming' ||
      (!resultDialog.hidden &&
        resultDialog.dataset.resultState === 'processing' &&
        !confirmationTimedOut)
    )
      return;
    lastClosedCheckout =
      checkoutState === 'ready' && storedCheckout
        ? { ...storedCheckout, scope: closedCheckoutScope() }
        : lastClosedCheckout;
    checkoutAttempt?.finish('closed');
    discardPreparedCheckout('closed');
    startingCheckout = false;
    const shouldStartPopupCooldown = context?.entry_page === 'launch';
    activeRequest += 1;
    abortController?.abort();
    abortController = null;
    clearPolling();
    if (checkoutState !== 'payment_success') clearSuccessCloseTimer();
    if (!retainReadyPaymentElement()) destroyPaymentElement();
    // 手动关闭会员弹窗后清理登录回跳意图并重置 CTA，避免下次入口继承 loading 状态。
    clearPurchaseIntent();
    setCtaLoading(false);
    checkout.hidden = true;
    resultDialog.hidden = true;
    offer.hidden = false;
    root.hidden = true;
    root.removeAttribute('data-checkout-state');
    lockPage(false);
    restoreShopifyChat();
    checkoutState = 'closed';
    context = null;
    idempotencyKey = '';
    // 已提交但未确认的付款必须保留原会话；普通关闭仍清理未提交的恢复标记。
    if (!keepUnconfirmedCheckout) setStoredCheckoutId('');
    previousFocus?.focus?.({ preventScroll: true });
    previousFocus = null;
    if (shouldStartPopupCooldown && popupSectionId) {
      markPopupClosed(popupSectionId);
    }
  }

  async function getAnalytics(entryPage = context?.entry_page) {
    try {
      const analytics =
        await window.jjbCheckoutRuntime?.getAnalytics?.(entryPage);
      if (analytics) return analytics;
    } catch {}
    return entryPage ? { entryPage } : {};
  }

  function prewarm() {
    if (designMode) return;
    try {
      window.jjbCheckoutRuntime?.prepareAnalytics?.();
    } catch {}
    void resolveCheckoutUiMode().then((uiMode) => {
      if (!usesHostedCheckoutPage(uiMode, context?.purpose))
        void prewarmAirwallex();
    });
  }

  function canReplaceEndedCheckout(data) {
    return data?.status === 'CANCELLED' || data?.status === 'EXPIRED';
  }

  function retireCheckoutIntent(key) {
    window.jjbCheckoutRuntime?.retireIntent?.(key);
    for (const [scope, intent] of fallbackCheckoutIntents) {
      if (intent.idempotencyKey === key) fallbackCheckoutIntents.delete(scope);
    }
  }

  async function checkoutBody(
    uiMode,
    requestId,
    attempt,
    current = { ...context },
    assignIntent = true,
  ) {
    const analytics = await attempt.measure('analytics', () =>
      getAnalytics(current.entry_page),
    );
    if (requestId !== null && requestId !== activeRequest)
      throw new DOMException('Checkout closed.', 'AbortError');
    const plan = current.plan_type === 'premium' ? 'PREMIUM' : 'PRO';
    const billingCycle = current.billing_cycle === 'yearly' ? 'YEAR' : 'MONTH';
    // 赠转付的意图键必须按实验模式分键：服务端 requestHash 纳入显式 uiMode，
    // 同键跨模式重放会被拒绝，且取消后的换键恢复依赖服务端先返回已终结意向。
    const intentUiMode =
      current.purpose === 'GIFT_RESUME'
        ? await resolveCheckoutUiMode()
        : uiMode;
    const intentOptions = {
      plan,
      billingCycle,
      uiMode: intentUiMode,
      purpose: current.purpose,
      giftId: current.giftId,
      environment: isTestMode() ? 'test' : 'prod',
      analytics,
      createSuccessUrl: () => getCheckoutSuccessUrl(current.plan_type),
    };
    let intent = window.jjbCheckoutRuntime?.getIntent?.(intentOptions);
    if (!intent) {
      // 共享脚本加载失败时仍可付款；同页同意图重试保持原请求身份。
      let consent;
      try {
        consent = window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
      } catch {}
      const scope = JSON.stringify(
        current.purpose === 'GIFT_RESUME'
          ? [
              window.theme?.customerId,
              intentOptions.environment,
              'GIFT_RESUME',
              current.giftId,
              plan,
              billingCycle,
              intentUiMode,
            ]
          : current.purpose === 'SETUP'
            ? [
                window.theme?.customerId,
                intentOptions.environment,
                consent,
                'SETUP',
              ]
            : [
                window.theme?.customerId,
                intentOptions.environment,
                consent,
                plan,
                billingCycle,
                uiMode,
                current.entry_page,
                window.location.href,
              ],
      );
      const now = Date.now();
      for (const [key, value] of fallbackCheckoutIntents) {
        if (value.expires <= now) fallbackCheckoutIntents.delete(key);
      }
      intent = fallbackCheckoutIntents.get(scope);
      if (!intent) {
        intent = {
          idempotencyKey:
            window.crypto?.randomUUID?.() || createCheckoutSourceHash(),
          successUrl: intentOptions.createSuccessUrl(),
          analytics,
          expires: ['SETUP', 'GIFT_RESUME'].includes(current.purpose)
            ? Number.MAX_SAFE_INTEGER
            : now + CHECKOUT_CACHE_TTL_MS,
        };
        fallbackCheckoutIntents.set(scope, intent);
      }
    }
    if (assignIntent) {
      idempotencyKey = intent.idempotencyKey;
      checkoutIntentExpiresAt = Number(intent.expires) || 0;
    }
    return {
      plan,
      billingCycle,
      uiMode,
      successUrl: intent.successUrl,
      idempotencyKey: intent.idempotencyKey,
      analytics: intent.analytics,
    };
  }

  function prepareCheckout(options = {}) {
    if (
      designMode ||
      !isLoggedIn() ||
      normalizeMembershipState(getThemeMembershipState()).plan !== 'BASIC' ||
      !window.jjbCheckoutRuntime?.getIntent ||
      getStoredCheckoutId() ||
      retainedPaymentSession ||
      startingCheckout ||
      ['checkout_loading', 'ready', 'submitting', 'confirming'].includes(
        checkoutState,
      )
    )
      return Promise.resolve(false);
    const current = checkoutContext(options);
    const scope = preparationScope(current);
    if (
      !scope ||
      current.action_type === 'upgrade' ||
      current.purpose !== 'PURCHASE'
    )
      return Promise.resolve(false);
    if (
      lastClosedCheckout?.intentExpiresAt > 0 &&
      lastClosedCheckout.intentExpiresAt <= Date.now() &&
      lastClosedCheckout.scope ===
        preparationScope({ ...current, action_type: 'purchase' })
    )
      return Promise.resolve(false);
    return resolveCheckoutUiMode().then((uiMode) => {
      if (usesHostedCheckoutPage(uiMode, current?.purpose)) return false;
      return prepareElementsCheckout(current, scope, options);
    });
  }

  function prepareElementsCheckout(current, scope, options = {}) {
    prewarm();
    if (
      pendingCheckout?.scope === scope &&
      pendingCheckout.expires > Date.now()
    ) {
      if (options.trustTarget)
        armCheckoutTrust(options.trustTarget, {
          freeze: options.freezeTrust === true,
        });
      return pendingCheckout.promise;
    }
    discardPreparedCheckout('changed');
    const startedAt = performance.now();
    const prepared = {
      scope,
      context: current,
      startedAt,
      expires: Date.now() + CHECKOUT_PREFETCH_MS,
      abort: new AbortController(),
      attempt: createCheckoutAttempt('ELEMENTS', 'prefetch', startedAt),
      data: null,
      checkout: null,
      body: null,
      membership: null,
      customerId: '',
      succeededAt: 0,
      discarded: false,
      failed: false,
      timer: undefined,
      traceId: undefined,
      responsePromise: null,
      checkoutPromise: null,
      promise: null,
    };
    pendingCheckout = prepared;
    if (options.trustTarget)
      armCheckoutTrust(options.trustTarget, {
        freeze: options.freezeTrust === true,
      });
    const valid = () =>
      pendingCheckout === prepared &&
      !prepared.discarded &&
      preparationScope(current) === scope;
    const guard = () => {
      if (!valid())
        throw new DOMException('Checkout preparation cancelled.', 'AbortError');
    };
    prepared.timer = window.setTimeout(() => {
      if (pendingCheckout === prepared) discardPreparedCheckout('expired');
    }, CHECKOUT_PREFETCH_MS);
    prepared.responsePromise = (async () => {
      const membership = await refreshCheckoutMembership(prepared.abort.signal);
      guard();
      if (
        requiresPaymentMethodUpdate(membership) ||
        membership.plan !== 'BASIC'
      )
        throw new Error('Membership is not eligible for preparation.');
      prepared.membership = membership;
      prepared.customerId = String(window.theme?.customerId || '');
      prepared.body = await checkoutBody(
        'ELEMENTS',
        null,
        prepared.attempt,
        current,
        false,
      );
      guard();
      const data = await prepared.attempt.measure('backend', () =>
        checkoutTimeout(
          requestMembership('/checkouts/prepare', {
            method: 'POST',
            signal: prepared.abort.signal,
            headers: { 'Idempotency-Key': prepared.body.idempotencyKey },
            body: prepared.body,
            onResponse: (fields) => {
              prepared.traceId = fields.traceId;
              prepared.attempt.bind(fields);
            },
          }),
          'checkout preparation',
        ),
      );
      guard();
      if (canReplaceEndedCheckout(data)) {
        // 只有服务端确认不可再支付，才允许淘汰旧幂等键。
        prepared.ended = true;
        retireCheckoutIntent(prepared.body.idempotencyKey);
      }
      if (
        data?.status !== 'ACTIVE' ||
        !data.clientSecret ||
        !(data.id || data.checkoutId) ||
        !(Date.parse(data.expiresAt) > Date.now() + CHECKOUT_PREFETCH_MS)
      )
        throw new Error('Checkout is not eligible for preparation.');
      prepared.data = { ...data, checkoutId: data.id || data.checkoutId };
      prepared.succeededAt = Date.now();
      prepared.attempt.bind({
        checkoutId: prepared.data.checkoutId,
        environment: data.environment,
      });
      return prepared.data;
    })();
    prepared.checkoutPromise = prepared.responsePromise.then(async (data) => {
      const sdk = await prepared.attempt.measure('sdk_load', () =>
        loadAirwallexSdk(data.sdkUrl),
      );
      guard();
      const initialized = await prepared.attempt.measure('sdk_init', () =>
        initializeAirwallex(
          sdk,
          data.environment || (isTestMode() ? 'demo' : 'prod'),
        ),
      );
      guard();
      let abandoned = false;
      const creating = Promise.resolve(
        initialized.billing.createCheckout({
          client_secret: data.clientSecret,
        }),
      );
      creating.then(
        (value) => {
          if (abandoned || !valid()) disposeCheckout(value);
        },
        () => {},
      );
      try {
        const value = await prepared.attempt.measure('sdk_checkout', () =>
          checkoutTimeout(creating, 'preparation'),
        );
        guard();
        prepared.checkout = value;
        return value;
      } catch (error) {
        abandoned = true;
        throw error;
      }
    });
    prepared.promise = prepared.checkoutPromise
      .then(() => {
        guard();
        prepared.attempt.finish('prepared');
        return true;
      })
      .catch((error) => {
        prepared.failed = true;
        prepared.attempt.finish(
          error?.name === 'AbortError' ? 'closed' : 'error',
        );
        if (pendingCheckout === prepared) discardPreparedCheckout('error');
        return false;
      });
    return prepared.promise;
  }

  async function takePreparedCheckout(data, attempt) {
    const prepared = pendingCheckout;
    if (!prepared) return null;
    const matches = () =>
      !prepared.discarded &&
      !prepared.failed &&
      prepared.scope === preparationScope(context) &&
      prepared.expires > Date.now() &&
      prepared.body?.idempotencyKey === idempotencyKey &&
      data.status === 'ACTIVE' &&
      prepared.data &&
      paymentSessionKey(prepared.data) === paymentSessionKey(data);
    if (!matches()) {
      discardPreparedCheckout('changed');
      return null;
    }
    try {
      const value = await attempt.measure(
        'prefetch_wait',
        () => prepared.checkoutPromise,
      );
      if (!matches() || pendingCheckout !== prepared) return null;
      pendingCheckout = null;
      window.clearTimeout(prepared.timer);
      prepared.checkout = null;
      prepared.data = null;
      prepared.attempt.record(
        'prefetch_use',
        performance.now() - prepared.startedAt,
        'success',
      );
      return value;
    } catch {
      return null;
    }
  }

  async function startCheckout() {
    if (
      startingCheckout ||
      ['checkout_loading', 'submitting', 'confirming'].includes(checkoutState)
    )
      return false;
    const startToken = {};
    startingCheckout = startToken;
    const startedAt = performance.now();
    setCtaLoading(true);
    try {
      return await initializeCheckout(startedAt);
    } finally {
      if (startingCheckout === startToken) {
        if (!root.hidden) setCtaLoading(false);
        startingCheckout = false;
      }
    }
  }

  function storePurchaseIntent() {
    const intent = createPurchaseIntent({
      entryPage: context.entry_page,
      returnUrl: getSafeReturnUrl(),
    });
    try {
      sessionStorage.setItem(
        PURCHASE_INTENT_STORAGE_KEY,
        JSON.stringify(intent),
      );
    } catch {}
    setCtaLoading(false);
    window.location.href = getLoginUrl(intent.return_url);
  }

  /**
   * 购买意图只在终态清除：用户显式关闭弹窗、已是付费会员不再恢复，
   * 或 Checkout 已成功创建（此后刷新由嵌入式 checkout 缓存或幂等回放兜底）。
   * 中途失败/超时/刷新时保留意图，保证登录回流可安全重试。
   */
  function clearPurchaseIntent() {
    try {
      sessionStorage.removeItem(PURCHASE_INTENT_STORAGE_KEY);
    } catch {}
  }

  function setCtaLoading(loading) {
    ctaLoading = loading;
    root.querySelectorAll('[data-jjb-membership-join]').forEach((button) => {
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      button.classList.toggle('is-loading', loading);
      button.innerHTML = loading
        ? '<span class="jjb-membership-offer__cta-spinner" aria-hidden="true"></span><span>Loading...</span>'
        : 'Join for $19.90/mo';
    });
  }

  function getStoredCheckout() {
    const customerId = String(window.theme?.customerId || '');
    let cached =
      checkoutRecovery?.customerId === customerId ? checkoutRecovery : null;
    try {
      if (!cached)
        cached = JSON.parse(sessionStorage.getItem(CHECKOUT_ID_STORAGE_KEY));
      const checkoutId = String(cached?.checkoutId || '');
      const requestedAt = Number(cached?.requestedAt || 0);
      if (
        !checkoutId ||
        (cached.customerId && cached.customerId !== customerId) ||
        (context &&
          cached.context &&
          cached.pendingConfirmation !== true &&
          !['SETUP', 'GIFT_RESUME'].includes(cached.context.purpose) &&
          (cached.context.plan_type !== context.plan_type ||
            cached.context.billing_cycle !== context.billing_cycle)) ||
        !Number.isFinite(requestedAt) ||
        requestedAt <= 0 ||
        (cached.pendingConfirmation !== true &&
          !['SETUP', 'GIFT_RESUME'].includes(cached.context?.purpose) &&
          checkoutState !== 'ready' &&
          checkoutState !== 'confirm_timeout' &&
          Date.now() - requestedAt > CHECKOUT_CACHE_TTL_MS)
      ) {
        setStoredCheckoutId('');
        return null;
      }
      if (
        cached.pendingConfirmation === true ||
        ['SETUP', 'GIFT_RESUME'].includes(cached.context?.purpose)
      )
        checkoutRecovery = cached;
      return cached;
    } catch {
      setStoredCheckoutId('');
      return null;
    }
  }

  function getStoredCheckoutId() {
    return String(getStoredCheckout()?.checkoutId || '');
  }

  function setStoredCheckoutId(checkoutId, pendingConfirmation = false) {
    const cached = checkoutId
      ? {
          checkoutId,
          requestedAt: Date.now(),
          customerId: String(window.theme?.customerId || ''),
          context,
          idempotencyKey,
          intentExpiresAt: checkoutIntentExpiresAt,
          ...(pendingConfirmation ? { pendingConfirmation: true } : {}),
        }
      : null;
    // 同页恢复还要覆盖Storage被浏览器拒绝的普通未提交表单。
    checkoutRecovery = cached;
    try {
      if (cached)
        sessionStorage.setItem(CHECKOUT_ID_STORAGE_KEY, JSON.stringify(cached));
      else sessionStorage.removeItem(CHECKOUT_ID_STORAGE_KEY);
    } catch {}
  }

  async function pollCheckout(
    checkoutId,
    requestId = activeRequest,
    restored = false,
  ) {
    if (requestId !== activeRequest || root.hidden) return;
    clearPolling();
    if (!pollingStartedAt) pollingStartedAt = Date.now();
    const queryAbort = new AbortController();
    abortController?.abort();
    abortController = queryAbort;
    try {
      const result = await checkoutTimeout(
        readCheckout(checkoutId, {
          signal: queryAbort.signal,
        }),
        'confirmation',
        Math.max(1, 60_000 - (Date.now() - pollingStartedAt)),
      );
      if (requestId !== activeRequest || root.hidden) return;
      applyCheckoutMode(result);
      const nextState = resolveCheckoutState(result?.status);
      if (nextState === 'payment_success') {
        await completeCheckoutResult(result, restored);
        return;
      }
      if (nextState === 'payment_failed') {
        retireCheckoutIntent(idempotencyKey);
        checkoutAttempt?.finish('terminal');
        latestPaymentError = {
          code: result?.code,
          message: result?.message || 'Payment failed. Please try again.',
        };
        setStoredCheckoutId('');
        checkoutState = 'payment_failed';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'failed',
          checkoutFailureMessage(
            result?.status,
            'Payment failed. Please try again.',
          ),
          'Retry',
        );
        return;
      }
      if (Date.now() - pollingStartedAt >= 60_000) {
        checkoutState = 'confirm_timeout';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'processing',
          'Payment is still being confirmed.',
          'Check status',
        );
        return;
      }
      const delay = Date.now() - pollingStartedAt < 10_000 ? 1000 : 2500;
      pollingTimer = window.setTimeout(
        () => pollCheckout(checkoutId, requestId, restored),
        delay,
      );
    } catch {
      if (requestId !== activeRequest || root.hidden) return;
      if (Date.now() - pollingStartedAt >= 60_000) {
        checkoutState = 'confirm_timeout';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'processing',
          'We could not confirm the result yet.',
          'Check status',
        );
      } else {
        pollingTimer = window.setTimeout(
          () => pollCheckout(checkoutId, requestId, restored),
          2500,
        );
      }
    } finally {
      queryAbort.abort();
      if (abortController === queryAbort) abortController = null;
    }
  }

  function syncCheckoutHeader() {
    if (isGiftResume()) {
      checkoutTitle.textContent = 'Resume membership';
      planLabel.textContent = `${context.plan_label} · ${context.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}`;
      billingLabel.textContent = context.scheduledStartsAt
        ? 'Starts on ' +
          new Date(context.scheduledStartsAt).toLocaleString() +
          ' · No charge today'
        : 'Starts when your gift ends · No charge today';
      priceLabel.textContent = context.display_price || '';
      checkoutSubtotal.hidden = true;
      checkoutAdjustment.hidden = true;
      return;
    }
    if (checkoutTitle)
      checkoutTitle.textContent = isSetupCheckout()
        ? 'Update payment method'
        : 'Confirm Membership';
    if (isSetupCheckout()) {
      planLabel.textContent = 'Update payment method';
      billingLabel.textContent = 'For your existing subscription';
      priceLabel.textContent = '';
      checkoutSubtotal.hidden = true;
      checkoutAdjustment.hidden = true;
      return;
    }
    planLabel.textContent = `${context.plan_label || 'Membership'} · ${context.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}`;
    billingLabel.textContent = `Billed ${context.billing_cycle}, starting today`;
    // 升级的应付价依赖按比例折算，静态首年优惠价必然不准：加载期留空，
    // 报价返回后由 syncCheckoutAmount 一次性写入真实套餐金额，避免价格闪烁。
    priceLabel.textContent =
      context.action_type === 'upgrade' ? '' : context.display_price || '';
    checkoutSubtotalAmount.textContent = '';
    checkoutSubtotal.hidden = true;
    checkoutAdjustmentAmount.textContent = '';
    checkoutAdjustment.hidden = true;
  }

  async function restoreCheckout(
    checkoutId,
    startedAt = performance.now(),
    { interactive = false } = {},
  ) {
    const stored = getStoredCheckout();
    const pendingConfirmation =
      stored?.checkoutId === checkoutId && stored.pendingConfirmation === true;
    if (stored?.checkoutId === checkoutId) {
      context = { ...context, ...stored.context };
      idempotencyKey = stored.idempotencyKey || '';
      checkoutIntentExpiresAt = Number(stored.intentExpiresAt) || 0;
    }
    const requestId = ++activeRequest;
    sdkLoadStartedAt = Date.now();
    checkoutAttempt?.finish('closed');
    const attempt = (checkoutAttempt = createCheckoutAttempt(
      'ELEMENTS',
      'restore',
      startedAt,
    ));
    abortController?.abort();
    const restoreAbort = new AbortController();
    abortController = restoreAbort;
    discardPreparedCheckout('restore');
    destroyPaymentElement();
    syncCheckoutHeader();
    resultDialog.hidden = true;
    checkout.hidden = false;
    offer.hidden = true;
    setCheckoutState(
      'checkout_loading',
      '<div><span class="jjb-membership-spinner" aria-hidden="true"></span><p>Loading secure checkout...</p></div>',
    );
    notifyCheckoutViewShown?.();
    notifyCheckoutViewShown = null;
    try {
      const data = await attempt.measure('restore', () =>
        checkoutTimeout(
          readCheckout(checkoutId, {
            signal: restoreAbort.signal,
            onResponse: (fields) => attempt.bind(fields),
          }),
          'restore',
        ),
      );
      if (requestId !== activeRequest || checkout.hidden) return;
      applyCheckoutMode(data);
      setStoredCheckoutId(checkoutId, pendingConfirmation);
      const nextState = resolveCheckoutState(data?.status);
      if (
        isGiftResume() &&
        !interactive &&
        !pendingConfirmation &&
        nextState !== 'payment_success' &&
        nextState !== 'payment_failed'
      ) {
        // 自动恢复（页面加载/bfcache 返回）不得唤起支付：HOSTED 不跳收银台、
        // ELEMENTS 不弹半屏，只清除恢复上下文并刷新会员状态；重试入口留给
        // 用户点击 Resume/Complete setup（interactive 路径）。先清存储再关层：
        // closeLayer 对赠转付目的会保留未确认会话标记。
        setStoredCheckoutId('');
        if (!root.hidden) {
          closeLayer();
        } else {
          checkoutAttempt?.finish('closed');
          checkoutState = 'closed';
          root.dataset.checkoutState = checkoutState;
        }
        window.dispatchEvent(new CustomEvent('jjb:membership-updated'));
        return;
      }
      // 其余赠转付恢复结果（成功/失败/确认）与手势重试的半屏绑卡需要弹层
      // 承接；唯一不开层的是手势重试的 HOSTED 跳转（马上离开本页）。
      const giftRedirectingToCashier =
        isGiftResume() && !pendingConfirmation && interactive && data.url;
      if (isGiftResume() && root.hidden && !giftRedirectingToCashier)
        openLayer();
      if (nextState === 'payment_success') {
        await completeCheckoutResult(data, true);
        return;
      }
      if (nextState === 'payment_failed') {
        retireCheckoutIntent(idempotencyKey);
        checkoutAttempt?.finish('terminal');
        latestPaymentError = {
          code: data?.code,
          message: data?.message || 'Payment failed. Please try again.',
        };
        setStoredCheckoutId('');
        checkoutState = 'payment_failed';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'failed',
          checkoutFailureMessage(
            data?.status,
            'Payment failed. Please try again.',
          ),
          'Retry',
        );
        return;
      }
      if (isGiftResume() && !pendingConfirmation && interactive && data.url) {
        // 用户主动重试的 HOSTED 收银台会话：重新跳转继续付款，
        // 不能落入确认轮询（会话未支付，轮询永远不会完成）。
        setStoredCheckoutId(checkoutId, pendingConfirmation);
        window.location.href = data.url;
        return;
      }
      if (
        String(data?.status || '').toUpperCase() === 'ACTIVE' &&
        !pendingConfirmation
      ) {
        if (!isSetupCheckout()) {
          const membership = await refreshCheckoutMembership(
            restoreAbort.signal,
          );
          if (requestId !== activeRequest || checkout.hidden) return;
          if (requiresPaymentMethodUpdate(membership)) {
            // 旧页面保存的未提交购买不能绕过当前欠费分流，修卡使用独立意图。
            setStoredCheckoutId('');
            context.purpose = 'SETUP';
            context.action_type = 'payment_method_setup';
            checkoutState = 'closed';
            await initializeCheckout(startedAt);
            return;
          }
        }
        if (!data?.clientSecret) throw new Error('Checkout is not ready.');
        await mountPaymentForm(
          { ...data, checkoutId: data.id || checkoutId },
          requestId,
          attempt,
        );
        return;
      }
      setStoredCheckoutId(checkoutId, true);
      checkoutState = 'confirming';
      root.dataset.checkoutState = checkoutState;
      showResult('processing', processingMessage());
      pollingStartedAt = 0;
      pollCheckout(checkoutId, activeRequest, true);
    } catch (error) {
      if (requestId !== activeRequest) return;
      attempt.finish('error');
      if (
        pendingConfirmation ||
        isSetupCheckout() ||
        (stored?.intentExpiresAt > 0 && stored.intentExpiresAt <= Date.now())
      ) {
        checkoutState = 'confirm_timeout';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'processing',
          'We could not confirm the result yet.',
          'Check status',
        );
        return;
      }
      setCheckoutState(
        'init_error',
        `<div>${error?.message || 'Unable to restore checkout.'}</div>`,
      );
    } finally {
      restoreAbort.abort();
      if (abortController === restoreAbort) abortController = null;
    }
  }

  function handlePaymentSubmitted(checkoutId) {
    setStoredCheckoutId(checkoutId, true);
    if (!isSetupCheckout() && !begunCheckoutIds.has(checkoutId)) {
      begunCheckoutIds.add(checkoutId);
      trackMembership('membership_begincheckout', {
        entry_page: context.entry_page,
        plan_type: context.plan_type,
        billing_cycle: context.billing_cycle,
        action_type: context.action_type,
        checkout_id: checkoutId,
      });
    }
    checkoutState = 'confirming';
    root.dataset.checkoutState = checkoutState;
    showResult('processing', processingMessage());
    pollingStartedAt = 0;
    pollCheckout(checkoutId);
  }

  async function mountPaymentForm(data, requestId, attempt) {
    const key = paymentSessionKey(data);
    const retained = retainedPaymentSession;
    const formReused = Boolean(
      retained &&
      retained === paymentSession &&
      retained.element === paymentElement &&
      retained.ready &&
      retained.reusable &&
      retained.key === key &&
      key &&
      retained.retainUntil > Date.now() &&
      String(data.status).toUpperCase() === 'ACTIVE',
    );
    if (!formReused) destroyPaymentElement();
    else {
      window.clearTimeout(retainedPaymentTimer);
      retainedPaymentSession = null;
    }
    attempt.bind({
      checkoutId: data.checkoutId,
      environment: data.environment || (isTestMode() ? 'demo' : 'prod'),
      formReused,
      prefetchHit: false,
    });
    syncCheckoutAmount(data);
    const isCurrent = () => requestId === activeRequest && !checkout.hidden;
    const guard = () => {
      if (!isCurrent())
        throw new DOMException('Checkout closed.', 'AbortError');
    };
    let phase = 'sdk_load';
    let element;
    let billingCheckout;
    let session;
    let abandoned = false;
    const dispose = (value) => {
      try {
        value?.unmount?.();
        value?.destroy?.();
      } catch {}
    };
    try {
      if (formReused) {
        session = retained;
        element = session.element;
        // 完整表单一直留在隐藏DOM中；这些初始化阶段无需再次执行。
        for (const reusedPhase of [
          'sdk_load',
          'sdk_init',
          'sdk_checkout',
          'element_create',
        ])
          attempt.record(reusedPhase, 0, 'success');
      } else {
        billingCheckout = await takePreparedCheckout(data, attempt);
        guard();
        attempt.bind({ prefetchHit: Boolean(billingCheckout) });
        if (billingCheckout) {
          for (const readyPhase of ['sdk_load', 'sdk_init', 'sdk_checkout'])
            attempt.record(readyPhase, 0, 'success');
        } else {
          const sdk = await attempt.measure(phase, () =>
            loadAirwallexSdk(data.sdkUrl),
          );
          guard();
          phase = 'sdk_init';
          const initialized = await attempt.measure(phase, () =>
            initializeAirwallex(
              sdk,
              data.environment || (isTestMode() ? 'demo' : 'prod'),
            ),
          );
          guard();
          if (!initialized?.billing?.createCheckout)
            throw new Error('Billing Checkout is unavailable.');
          phase = 'sdk_checkout';
          const creatingCheckout = Promise.resolve(
            initialized.billing.createCheckout({
              client_secret: data.clientSecret || data.client_secret,
            }),
          );
          creatingCheckout.then(
            (value) => {
              if (abandoned || !isCurrent()) disposeCheckout(value);
            },
            () => {},
          );
          billingCheckout = await attempt.measure(phase, () =>
            checkoutTimeout(creatingCheckout, phase),
          );
          guard();
        }
        paymentCheckout = billingCheckout;
        phase = 'element_create';
        const creating = Promise.resolve(
          billingCheckout.createElement('paymentForm'),
        );
        // SDK没有AbortSignal；迟到的元素必须销毁，不能覆盖下一次打开的元素。
        creating.then(
          (value) => {
            if (abandoned || !isCurrent()) dispose(value);
          },
          () => {},
        );
        element = await attempt.measure(phase, () =>
          checkoutTimeout(creating, phase),
        );
        guard();
        if (!element) throw new Error('Payment component is unavailable.');
        paymentElement = element;
        session = paymentSession = {
          element,
          key,
          ready: false,
          reusable: true,
          binding: null,
          retainUntil: 0,
        };
        // 每个SDK元素只注册一次。保留期间关闭binding，迟到回调不能影响新尝试。
        element.on?.('ready', () => {
          if (paymentSession !== session) return;
          session.ready = true;
          session.binding?.ready();
        });
        element.on?.('success', () => {
          if (paymentSession !== session) return;
          session.reusable = false;
          session.binding?.success();
        });
        element.on?.('error', (event) => {
          if (paymentSession !== session) return;
          session.reusable = false;
          session.binding?.error(event);
        });
      }
      let ready = false;
      let readyAllowed = false;
      let readyAt = 0;
      const onReady = () => {
        if (!isCurrent() || ready || !readyAllowed) return;
        ready = true;
        window.clearTimeout(readyTimer);
        setCheckoutState('ready', '');
        mount.hidden = false;
        endReadyWait?.('success');
        endReadyWait = undefined;
        attempt.finish('ready');
        trackMembership(
          'membership_sdk_loaded',
          {
            load_duration_ms: Math.max(0, Date.now() - sdkLoadStartedAt),
            form_reused: formReused,
          },
          { monitor: true },
        );
      };
      session.binding = {
        ready: onReady,
        success: () => {
          if (isCurrent()) handlePaymentSubmitted(data.checkoutId);
        },
        error: (event) => {
          if (!isCurrent()) return;
          if (event?.code === 'customerFormValidationFailed') {
            setCheckoutState(
              'ready',
              '<div>Please complete your address details to continue.</div>',
            );
            mount.hidden = false;
            return;
          }
          latestPaymentError = event;
          attempt.finish('error');
          trackMembership(
            'membership_popup_pay_error',
            getAirwallexErrorParams(event, data.checkoutId),
            { monitor: true },
          );
          setCheckoutState('payment_failed', '');
          showResult('failed', 'Payment failed. Please try again.', 'Retry');
        },
      };
      phase = 'presentation';
      // 保留移动端动画结束后mount，避免iOS iframe可见但无法点击。
      await attempt.measure(phase, () =>
        waitForCheckoutPresentation(requestId),
      );
      guard();
      phase = 'iframe_ready';
      readyAllowed = true;
      readyAt = performance.now();
      endReadyWait = (outcome) =>
        attempt.record('iframe_ready', performance.now() - readyAt, outcome);
      readyTimer = window.setTimeout(() => {
        if (!isCurrent() || ready) return;
        activeRequest += 1;
        latestSdkError = new Error('Payment form timed out. Please try again.');
        attempt.finish('error');
        endReadyWait?.('error');
        endReadyWait = undefined;
        destroyPaymentElement();
        setCheckoutState(
          'init_error',
          '<div>Unable to load the payment form. Please try again.</div>',
        );
      }, 20_000);
      if (formReused) onReady();
      else element.mount?.(paymentFormHost.id);
    } catch (error) {
      abandoned = true;
      if (billingCheckout && paymentCheckout !== billingCheckout)
        disposeCheckout(billingCheckout);
      if (element && paymentElement !== element) dispose(element);
      if (error?.name === 'AbortError' || !isCurrent()) return;
      destroyPaymentElement();
      latestSdkError = error;
      trackMembership(
        'membership_popup_sdk_error',
        { phase, ...getAirwallexErrorParams(error, data.checkoutId) },
        { monitor: true },
      );
      throw error;
    }
  }

  async function initializeCheckout(startedAt = performance.now()) {
    if (
      checkoutState === 'checkout_loading' ||
      checkoutState === 'submitting' ||
      checkoutState === 'confirming'
    )
      return;
    // HOSTED（对照组/未命中）不进入半屏支付视图，也不展示 Redirecting 弹窗：
    // 保持在当前页面由按钮 loading 承接等待，准备好后直接跳转收银台。
    // Gift resume 始终走内嵌流程，由后续 gift 校验与 /gift/resume 处理。
    if (
      usesHostedCheckoutPage(await resolveCheckoutUiMode(), context?.purpose)
    ) {
      return redirectToAirwallexCheckout({ source: 'new' });
    }
    const storedCheckout = getStoredCheckout();
    // 换挡：赠转付点击的档位/周期与存储会话不一致时，旧意向让位走新建
    // （后端会取消旧意向建立新意向）；View 入口（带 signupId）不点击具体
    // 档位，保持恢复既有签约。
    if (
      isGiftResume() &&
      !context.signupId &&
      storedCheckout &&
      (storedCheckout.context?.plan_type !== context.plan_type ||
        storedCheckout.context?.billing_cycle !== context.billing_cycle)
    ) {
      setStoredCheckoutId('');
    }
    const storedCheckoutId =
      getStoredCheckoutId() || (isGiftResume() ? context.signupId : null);
    if (storedCheckoutId) {
      // 点击驱动的恢复允许重新唤起支付（跳收银台/弹半屏绑卡）。
      return restoreCheckout(storedCheckoutId, startedAt, {
        interactive: true,
      });
    }
    const trust = consumeCheckoutTrust();
    const requestId = ++activeRequest;
    sdkLoadStartedAt = Date.now();
    let attempt;
    if (!retainedPaymentSession) destroyPaymentElement();
    latestSdkError = null;
    latestPaymentError = null;
    abortController?.abort();
    const requestAbort = (abortController = new AbortController());
    const guard = () => {
      if (
        requestId !== activeRequest ||
        checkout.hidden ||
        requestAbort.signal.aborted
      )
        throw new DOMException('Checkout closed.', 'AbortError');
    };
    offer.hidden = true;
    resultDialog.hidden = true;
    checkout.hidden = false;
    syncCheckoutHeader();
    setCheckoutState(
      'checkout_loading',
      '<div><span class="jjb-membership-spinner" aria-hidden="true"></span><p>Loading secure checkout...</p></div>',
    );
    notifyCheckoutViewShown?.();
    notifyCheckoutViewShown = null;
    focusDialog();
    let checkoutResponseValidated = false;
    let checkoutRefunded = false;
    try {
      const membership =
        trust.skipMe && trust.membership
          ? trust.membership
          : await refreshCheckoutMembership(requestAbort.signal);
      guard();
      // Billing paymentForm只提供ready/success/error。关闭后付款仍可能完成；
      // 用户再次选择同一目标时，先确认该页刚关闭的原付款，不重走同档升级。
      const closed = lastClosedCheckout;
      if (closed?.scope !== closedCheckoutScope()) lastClosedCheckout = null;
      const expiredClosedIntent =
        closed?.intentExpiresAt > 0 && closed.intentExpiresAt <= Date.now();
      if (
        closed?.checkoutId &&
        closed.customerId === String(window.theme?.customerId || '') &&
        closed.context?.purpose !== 'SETUP' &&
        !isSetupCheckout() &&
        closed.scope &&
        closed.scope === closedCheckoutScope() &&
        !requiresPaymentMethodUpdate(membership) &&
        (expiredClosedIntent ||
          (isActivePaidMembership(membership) &&
            membership.plan === String(context.plan_type).toUpperCase() &&
            normalizeCheckoutBillingCycle(membership.billingCycle) ===
              context.billing_cycle))
      ) {
        discardPreparedCheckout('restore');
        let completed;
        try {
          completed = await requestMembership(
            `/checkouts/${encodeURIComponent(closed.checkoutId)}`,
            { signal: requestAbort.signal },
          );
        } catch (error) {
          guard();
          if (!expiredClosedIntent) throw error;
          idempotencyKey = closed.idempotencyKey || '';
          checkoutIntentExpiresAt = closed.intentExpiresAt;
          setStoredCheckoutId(closed.checkoutId);
          checkoutState = 'confirm_timeout';
          root.dataset.checkoutState = checkoutState;
          showResult(
            'processing',
            'We could not confirm the result yet.',
            'Check status',
          );
          return;
        }
        guard();
        if (resolveCheckoutState(completed?.status) === 'payment_success') {
          const confirmedMembership = expiredClosedIntent
            ? await refreshCheckoutMembership(requestAbort.signal)
            : membership;
          guard();
          if (
            isActivePaidMembership(confirmedMembership) &&
            confirmedMembership.plan ===
              String(context.plan_type).toUpperCase() &&
            normalizeCheckoutBillingCycle(confirmedMembership.billingCycle) ===
              context.billing_cycle
          ) {
            idempotencyKey = closed.idempotencyKey || '';
            await completeCheckoutResult(completed);
            return;
          }
          lastClosedCheckout = null;
        }
        if (
          expiredClosedIntent &&
          ['CREATING', 'PROCESSING'].includes(completed?.status)
        ) {
          idempotencyKey = closed.idempotencyKey || '';
          setStoredCheckoutId(closed.checkoutId, true);
          checkoutState = 'confirming';
          root.dataset.checkoutState = checkoutState;
          showResult('processing', processingMessage());
          pollingStartedAt = 0;
          void pollCheckout(closed.checkoutId);
          return;
        }
      }
      if (isGiftResume() || membership.membershipSource === 'GIFT') {
        if (
          membership.membershipSource !== 'GIFT' ||
          !membership.giftEndsAt ||
          Date.parse(membership.giftEndsAt) <= Date.now() ||
          membership.resumeVisible === false
        )
          throw new Error(
            'Your gift membership has changed. Please refresh your membership.',
          );
        context.purpose = 'GIFT_RESUME';
        context.giftId = membership.giftId;
        context.scheduledStartsAt = membership.giftEndsAt;
        discardPreparedCheckout('changed');
      }
      if (
        !isGiftResume() &&
        isSetupCheckout() &&
        !requiresPaymentMethodUpdate(membership)
      )
        throw new Error(
          'Your membership status has changed. Please review your membership before continuing.',
        );
      if (!isGiftResume())
        context.purpose = requiresPaymentMethodUpdate(membership)
          ? 'SETUP'
          : 'PURCHASE';
      if (isSetupCheckout()) {
        context.action_type = 'payment_method_setup';
        discardPreparedCheckout('changed');
      }
      syncCheckoutHeader();
      attempt = checkoutAttempt = createCheckoutAttempt(
        'ELEMENTS',
        'new',
        startedAt,
      );
      void prewarmAirwallex();
      if (!isSetupCheckout())
        trackCheckoutMonitor('membership_popup_checkout_start');
      let body = await checkoutBody('ELEMENTS', requestId, attempt);
      guard();
      const prepared = pendingCheckout;
      const matchingPreparation =
        prepared &&
        !prepared.failed &&
        !prepared.discarded &&
        prepared.scope === preparationScope(context) &&
        prepared.expires > Date.now() &&
        JSON.stringify(prepared.body) === JSON.stringify(body);
      if (prepared && !matchingPreparation) discardPreparedCheckout('changed');
      if (matchingPreparation) window.clearTimeout(prepared.timer);
      // 点击时仍在飞的创建请求直接接管；已返回的预创建则重新校验付款状态与金额。
      const pendingResponse =
        matchingPreparation && !prepared.data ? prepared.responsePromise : null;
      const data = await attempt.measure('backend', async () => {
        let replacedEndedCheckout = false;
        const replaceEndedIntent = async () => {
          guard();
          retireCheckoutIntent(body.idempotencyKey);
          discardPreparedCheckout('terminal');
          body = await checkoutBody('ELEMENTS', requestId, attempt);
          guard();
          replacedEndedCheckout = true;
        };
        const create = async () => {
          guard();
          return checkoutTimeout(
            requestMembership(
              isGiftResume()
                ? '/gift/resume'
                : isSetupCheckout()
                  ? '/subscription/payment-method/setup'
                  : '/checkouts',
              {
                method: 'POST',
                signal: requestAbort.signal,
                headers: { 'Idempotency-Key': body.idempotencyKey },
                onResponse: (fields) => attempt.bind(fields),
                ...(isGiftResume()
                  ? {
                      body: {
                        plan: body.plan,
                        billingCycle: body.billingCycle,
                        idempotencyKey: body.idempotencyKey,
                        // 赠转付与购买共用收银台形态实验：服务端按该模式建会话。
                        uiMode: await resolveCheckoutUiMode(),
                      },
                    }
                  : isSetupCheckout()
                    ? {}
                    : { body }),
              },
            ),
            'checkout creation',
          );
        };
        let response;
        if (pendingResponse) {
          try {
            response = await pendingResponse;
            attempt.bind({ traceId: prepared.traceId });
          } catch {
            guard();
            // 结果未知或旧接口缺失时保留原键；明确取消/过期才换键。
            if (prepared.ended) await replaceEndedIntent();
          }
        }
        if (
          !response &&
          trust.skipPost &&
          matchingPreparation &&
          prepared?.data?.status === 'ACTIVE' &&
          (prepared.data.clientSecret || prepared.data.client_secret) &&
          !isSetupCheckout()
        ) {
          attempt.bind({ traceId: prepared.traceId });
          return prepared.data;
        }
        response ??= await create();
        if (isGiftResume()) response = giftCheckoutData(response);
        guard();
        if (!replacedEndedCheckout && canReplaceEndedCheckout(response)) {
          await replaceEndedIntent();
          response = await create();
          if (isGiftResume()) response = giftCheckoutData(response);
          guard();
        }
        return response;
      });
      if (requestId !== activeRequest || checkout.hidden) return;
      applyCheckoutMode(data);
      // HOSTED 实验组的赠转付未预开弹层：响应没有收银台 url（直约完成、
      // 需半屏绑卡、确认中或已终结）时先补开弹层，避免在隐藏层里
      // 静默挂载表单或静默完成。
      if (isGiftResume() && root.hidden && !data.url) openLayer();
      if (
        isSetupCheckout() &&
        resolveCheckoutState(data?.status) === 'payment_success'
      ) {
        await completeCheckoutResult(data);
        return;
      }
      if (CHECKOUT_TERMINAL_STATES.includes(data?.status)) {
        checkoutRefunded = data.status === 'REFUNDED';
        retireCheckoutIntent(idempotencyKey);
        throw new Error(
          checkoutFailureMessage(
            data?.status,
            'Checkout has ended. Please try again.',
          ),
        );
      }
      const checkoutData = {
        ...data,
        checkoutId: data?.id || data?.checkoutId,
      };
      if (isGiftResume() && data.url) {
        // HOSTED 收银台会话：整页跳转，回跳后由会员页展示预约状态；
        // ELEMENTS 半屏不会走到这里（无 url、有 clientSecret）。
        checkoutResponseValidated = true;
        clearPurchaseIntent();
        setStoredCheckoutId(checkoutData.checkoutId);
        attempt.bind({
          checkoutId: data.checkout?.id || checkoutData.checkoutId,
        });
        window.location.href = data.url;
        return;
      }
      if (
        checkoutData.checkoutId &&
        (data.status === 'PROCESSING' ||
          (isSetupCheckout() &&
            (data.status !== 'ACTIVE' || !checkoutData.clientSecret)))
      ) {
        // 原付款已受理而权益仍在应用时，只确认原ID，不重新展示付款或Hosted重试。
        setStoredCheckoutId(checkoutData.checkoutId, true);
        clearPurchaseIntent();
        checkoutState = 'confirming';
        root.dataset.checkoutState = checkoutState;
        showResult('processing', processingMessage());
        pollingStartedAt = 0;
        void pollCheckout(checkoutData.checkoutId);
        return;
      }
      if (!checkoutData.checkoutId || !checkoutData.clientSecret)
        throw new Error('Checkout is not ready.');
      checkoutResponseValidated = true;
      clearPurchaseIntent();
      setStoredCheckoutId(checkoutData.checkoutId);
      attempt.bind({
        checkoutId: checkoutData.checkoutId,
        environment:
          checkoutData.environment || (isTestMode() ? 'demo' : 'prod'),
      });
      if (!isSetupCheckout())
        trackCheckoutMonitor('membership_popup_checkout_success', {
          checkoutId: checkoutData.checkoutId,
        });
      await mountPaymentForm(checkoutData, requestId, attempt);
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== activeRequest) return;
      discardPreparedCheckout('error');
      destroyPaymentElement();
      attempt?.finish('error');
      if (!checkoutResponseValidated && !isSetupCheckout()) {
        trackCheckoutMonitor('membership_popup_checkout_fail', {
          message: error?.message,
        });
      }
      showCheckoutRequestError(error);
      if (checkoutRefunded) {
        showRefundedCheckout();
      } else {
        setCheckoutState(
          'init_error',
          `<div>${error?.message || 'Unable to initialize checkout.'}</div>`,
        );
      }
    }
  }

  function open(options = {}, { showOffer = true, prepare = true } = {}) {
    if (
      startingCheckout ||
      checkoutState === 'checkout_loading' ||
      checkoutState === 'submitting' ||
      checkoutState === 'confirming'
    ) {
      return true;
    }
    context = checkoutContext(options);
    prewarm();
    const entryPage = context.entry_page;
    openLayer();
    offer.hidden = !showOffer;
    checkout.hidden = true;
    resultDialog.hidden = true;
    if (showOffer) {
      trackMembership(
        'membership_plan_impression',
        {
          entry_page: entryPage,
          style: 'popup',
        },
        { statsig: true },
      );
      if (prepare) void prepareCheckout(context);
    }
    return true;
  }

  function openCheckout(options = {}) {
    if (designMode) {
      open(options);
      return Promise.resolve(true);
    }
    if (
      startingCheckout ||
      ['checkout_loading', 'submitting', 'confirming'].includes(checkoutState)
    )
      return Promise.resolve(true);
    // 返回 Promise 的 resolve 时机按模式区分：
    // HOSTED 在跳转触发后；半屏在弹窗弹起时即返回（入口按钮 loading 到此为止），
    // startCheckout 结束作为兜底，避免视图未弹起时永久悬挂。
    return resolveCheckoutUiMode().then((uiMode) => {
      let settleShown;
      const shown = new Promise((resolve) => {
        settleShown = resolve;
      });
      // HOSTED（对照组/未命中）不打开任何弹窗，当前页面按钮 loading，
      // 准备好后直接跳转收银台。赠转付 HOSTED 组同理：创建在后台进行，
      // 拿到收银台 url 整页跳转，预开半屏只会闪现 Loading 壳再跳走。
      // context 必须先于分支判定刷新：否则绑卡类流程（SETUP/GIFT_RESUME）
      // 在 HOSTED 组会误判为普通购买，落入不开弹窗分支。
      context = checkoutContext(options);
      const embeddedView = opensEmbeddedCheckoutView(uiMode, context.purpose);
      if (embeddedView) {
        notifyCheckoutViewShown = settleShown;
        open(options, { showOffer: false });
      }
      context.action_type =
        options.action_type ||
        (isActivePaidMembership(getThemeMembershipState())
          ? 'upgrade'
          : 'purchase');
      const kickoff = startCheckout();
      Promise.resolve(kickoff)
        .catch(() => {})
        .finally(() => {
          notifyCheckoutViewShown = null;
          settleShown();
        });
      // 错误已由弹窗内部经 toast / init_error 呈现，这里始终保持 fulfilled，
      // 与旧版同步返回 true 的调用方契约一致。
      return embeddedView
        ? shown.then(() => true)
        : Promise.resolve(kickoff).then(
            () => true,
            () => true,
          );
    });
  }

  root.querySelectorAll('[data-jjb-membership-close]').forEach((button) => {
    button.addEventListener('click', closeLayer);
  });
  offer.addEventListener('click', (event) => {
    if (isMembershipPopupBackdropClick(event.target, event.currentTarget)) {
      closeLayer();
    }
  });
  root.querySelectorAll('[data-jjb-membership-join]').forEach((button) => {
    const arm = (event) =>
      armCheckoutTrust(button, { freeze: event?.type === 'pointerdown' });
    button.addEventListener('pointerenter', arm);
    button.addEventListener('focus', arm);
    button.addEventListener('pointerdown', arm);
    button.addEventListener('click', async () => {
      if (ctaLoading) return;
      setCtaLoading(true);
      trackMembership(
        'membership_popup_click',
        {
          entry_page: context?.entry_page,
          plan_type: 'Pro',
          billing_cycle: 'monthly',
          isLogin: isLoggedIn(),
        },
        { monitor: true, statsig: true },
      );
      if (!isLoggedIn()) {
        storePurchaseIntent();
        return;
      }
      await startCheckout();
    });
  });
  checkoutClose.addEventListener('click', closeLayer);
  resultClose.addEventListener('click', closeLayer);
  function setCheckoutRetryLoading(loading) {
    retry.disabled = loading;
    retry.setAttribute('aria-busy', loading ? 'true' : 'false');
    retry.innerHTML = loading
      ? '<span class="jjb-membership-checkout__retry-spinner" aria-hidden="true"></span><span>Try again</span>'
      : 'Try again';
  }

  function setResultRetryLoading(loading) {
    resultRetry.disabled = loading;
    resultRetry.setAttribute('aria-busy', loading ? 'true' : 'false');
    resultRetry.innerHTML = loading
      ? '<span class="jjb-membership-result__retry-spinner" aria-hidden="true"></span><span>Retry</span>'
      : 'Retry';
  }

  async function redirectToAirwallexCheckout({
    keepCheckoutError = false,
    source = 'fallback',
  } = {}) {
    if (!context) return false;
    if (isSetupCheckout()) {
      checkoutState = 'closed';
      await initializeCheckout();
      return false;
    }
    const requestId = ++activeRequest;
    const attempt = createCheckoutAttempt('HOSTED', source);
    checkoutAttempt = attempt;
    abortController?.abort();
    abortController = new AbortController();
    destroyPaymentElement();
    setStoredCheckoutId('');
    let checkoutRefunded = false;
    try {
      const membership = await refreshCheckoutMembership(
        abortController.signal,
      );
      if (requestId !== activeRequest) return false;
      if (
        membership.membershipSource === 'GIFT' ||
        requiresPaymentMethodUpdate(membership)
      ) {
        context.purpose =
          membership.membershipSource === 'GIFT' ? 'GIFT_RESUME' : 'SETUP';
        context.action_type = 'payment_method_setup';
        checkoutState = 'closed';
        await initializeCheckout();
        return false;
      }
      trackCheckoutMonitor('membership_popup_checkout_start');
      const body = await checkoutBody('HOSTED', requestId, attempt);
      const data = await attempt.measure('backend', () =>
        checkoutTimeout(
          requestMembership('/checkouts', {
            method: 'POST',
            signal: abortController.signal,
            headers: { 'Idempotency-Key': body.idempotencyKey },
            onResponse: (fields) => attempt.bind(fields),
            body,
          }),
          'checkout creation',
        ),
      );
      if (requestId !== activeRequest) return false;
      if (CHECKOUT_TERMINAL_STATES.includes(data?.status)) {
        checkoutRefunded = data.status === 'REFUNDED';
        retireCheckoutIntent(idempotencyKey);
        throw new Error(
          checkoutFailureMessage(
            data?.status,
            'Checkout has ended. Please try again.',
          ),
        );
      }
      const url = getAirwallexPaymentUrl(data);
      if (!/^https:\/\//i.test(url))
        throw new Error('Airwallex payment URL is unavailable.');
      const checkoutId = data?.id || data?.checkoutId;
      attempt.bind({ checkoutId, environment: isTestMode() ? 'demo' : 'prod' });
      await Promise.all([
        trackCheckoutMonitor('membership_popup_checkout_success', {
          checkoutId,
        }),
        trackCheckoutMonitor('membership_checkout_redirect', {
          checkoutId,
          action: context.action_type === 'upgrade' ? 'upgrade' : 'buy',
          plan: body.plan,
          billingCycle: body.billingCycle,
        }),
        attempt.finish('redirect'),
      ]);
      if (requestId !== activeRequest) return false;
      // Checkout 已创建且即将跳转：此时清除购买意图，刷新由幂等回放兜底。
      clearPurchaseIntent();
      window.zlog?.flushTracking?.();
      window.location.href = url;
      return true;
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== activeRequest)
        return false;
      attempt.finish('error');
      trackCheckoutMonitor('membership_popup_checkout_fail', {
        message: error?.message,
      });
      showCheckoutRequestError(error);
      if (checkoutRefunded) {
        showRefundedCheckout();
      } else if (!keepCheckoutError) {
        resultDialog.hidden = true;
        offer.hidden = false;
        checkoutState = 'closed';
        root.dataset.checkoutState = 'closed';
      }
      return false;
    }
  }

  retry.addEventListener('click', async () => {
    if (checkoutState === 'confirm_timeout') {
      const checkoutId = getStoredCheckoutId();
      if (checkoutId) {
        activeRequest += 1;
        setCheckoutState(
          'confirming',
          '<div><span class="jjb-membership-spinner" aria-hidden="true"></span><p>Checking payment status...</p></div>',
        );
        pollingStartedAt = 0;
        pollCheckout(checkoutId, activeRequest, true);
      }
      return;
    }
    trackMembership(
      'membership_sdk_retry',
      getAirwallexErrorParams(latestSdkError),
      { monitor: true },
    );
    setCheckoutRetryLoading(true);
    await redirectToAirwallexCheckout({ keepCheckoutError: true });
    setCheckoutRetryLoading(false);
  });
  resultRetry.addEventListener('click', async () => {
    if (checkoutState === 'confirm_timeout') {
      const checkoutId = getStoredCheckoutId();
      if (checkoutId) {
        // 只读失败尚不代表已提交；再次读取原会话，ACTIVE可恢复原表单。
        if (
          getStoredCheckout()?.pendingConfirmation !== true &&
          !isSetupCheckout()
        ) {
          await restoreCheckout(checkoutId);
          return;
        }
        activeRequest += 1;
        checkoutState = 'confirming';
        root.dataset.checkoutState = checkoutState;
        showResult('processing', processingMessage());
        pollingStartedAt = 0;
        pollCheckout(checkoutId, activeRequest, true);
      }
      return;
    }
    if (checkoutState === 'payment_failed') {
      if (isGiftResume()) context.signupId = null;
      if (!isSetupCheckout())
        trackMembership(
          'membership_pay_retry',
          getAirwallexErrorParams(latestPaymentError),
          { monitor: true },
        );
      setResultRetryLoading(true);
      await redirectToAirwallexCheckout({ keepCheckoutError: true });
      setResultRetryLoading(false);
      return;
    }
    if (!isSetupCheckout()) {
      setStoredCheckoutId('');
      idempotencyKey = '';
    }
    checkoutAttempt = undefined;
    checkoutState = 'closed';
    resultDialog.hidden = true;
    initializeCheckout();
  });
  window.addEventListener('resize', syncView);
  document.addEventListener('keydown', (event) => {
    if (root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeLayer();
      return;
    }
    if (event.key !== 'Tab') return;
    const dialog = visibleDialog();
    const items = Array.from(
      dialog?.querySelectorAll(
        'button:not([disabled]), a[href], input, [tabindex="0"]',
      ) || [],
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const returnParams = new URLSearchParams(window.location.search);
  const returnedCheckoutId =
    !designMode && isLoggedIn() && returnParams.get('checkout') === 'success'
      ? returnParams.get('checkoutId')
      : '';
  const storedCheckoutId = designMode
    ? null
    : returnedCheckoutId || getStoredCheckoutId();
  window.addEventListener('pagehide', () =>
    discardPreparedCheckout('pagehide'),
  );
  document.addEventListener('visibilitychange', () => {
    if (checkoutTrust && document.visibilityState !== 'visible')
      checkoutTrust.visibilityBroken = true;
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || root.hidden || !context || !isLoggedIn()) return;
    if (checkoutState === 'payment_success') {
      // 返回缓存保留的是历史付款成功，不代表离开期间会员身份没有变化。
      if (!isSetupCheckout()) {
        showResult(
          'success',
          'This payment was confirmed. Your current membership details will refresh.',
        );
        window.dispatchEvent(new CustomEvent('jjb:membership-updated'));
      }
      return;
    }
    const checkoutId = getStoredCheckoutId();
    if (checkoutId) {
      clearPolling();
      void restoreCheckout(checkoutId);
    }
  });
  document.addEventListener('visitorConsentCollected', () =>
    discardPreparedCheckout('changed'),
  );
  if (storedCheckoutId) {
    context = {
      entry_page: 'direct',
      plan_type: 'pro',
      billing_cycle: 'monthly',
      plan_label: 'Membership',
      action_type: 'purchase',
    };
    try {
      const cached = JSON.parse(
        sessionStorage.getItem(CHECKOUT_ID_STORAGE_KEY),
      );
      if (!returnedCheckoutId || cached?.checkoutId === returnedCheckoutId) {
        context = { ...context, ...cached?.context };
        idempotencyKey = cached?.idempotencyKey || '';
      }
    } catch {}
    if (returnedCheckoutId) {
      // 外部验证回跳可以跨标签或丢失存储，先以原 ID 查询服务端，不推断付款目的。
      setStoredCheckoutId(storedCheckoutId, true);
      returnParams.delete('checkout');
      returnParams.delete('checkoutId');
      const query = returnParams.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname +
          (query ? '?' + query : '') +
          window.location.hash,
      );
    }
    // 赠转付不预开弹层：未支付会话的自动恢复会静默清理退出，预开只会
    // 闪现半屏壳再关掉；是否开层由 restoreCheckout 按恢复结果决定。
    if (context.purpose !== 'GIFT_RESUME') openLayer();
    restoreCheckout(storedCheckoutId);
  } else if (isLoggedIn()) {
    let intent;
    try {
      intent = parsePurchaseIntent(
        sessionStorage.getItem(PURCHASE_INTENT_STORAGE_KEY),
      );
    } catch {}
    if (intent && !isActivePaidMembership(getThemeMembershipState())) {
      // 意图先保留：Checkout 成功创建或用户显式关闭时才清除，
      // 中途失败/超时/刷新后刷新页面仍可安全重试。
      open(
        {
          entry_page: intent.entry_page,
          plan_type: 'pro',
          billing_cycle: 'monthly',
        },
        { prepare: false },
      );
      setCtaLoading(true);
      void mergeAnonymousActivityAfterLogin();
      void startCheckout();
    } else if (intent) {
      // 已是付费会员：不再恢复购买，属于终态，此时才清除意图。
      clearPurchaseIntent();
    }
  }

  return {
    open,
    openCheckout,
    prewarm,
    prepareCheckout,
    close: closeLayer,
    designMode,
    hasOpened: () => hasOpened,
    canAutoOpen: () =>
      !hasOpened &&
      (!popupSectionId || shouldShowPopup(popupSectionId, popupDelayDays)),
  };
}

function recordExperimentGroup(
  experimentName,
  key,
  value,
  groupIds,
  unknownGroupId,
) {
  const groupId = groupIds[String(value)] ?? unknownGroupId;
  window.zlog?.recordExperimentGroup?.(`${experimentName}:${key}`, groupId);
}

function getPopupSection() {
  return (
    document.querySelector('.newsletter-popup--original') ||
    document.querySelector('.newsletter-popup--v2')
  );
}

function getDelaySeconds(el) {
  return (parseInt(el.dataset.delaySeconds, 10) || 5) * 1000;
}

function getDelayDays(el) {
  return parseInt(el.dataset.delayDays, 10) || 30;
}

function shouldShowPopup(sectionId, delayDays) {
  try {
    const key = STORAGE_KEY_PREFIX + sectionId;
    const lastClosed = localStorage.getItem(key);
    if (!lastClosed) return true;
    const daysSinceClosed =
      (Date.now() - parseInt(lastClosed, 10)) / (1000 * 60 * 60 * 24);
    return daysSinceClosed >= delayDays;
  } catch {
    return true;
  }
}

function markPopupClosed(sectionId) {
  try {
    const key = STORAGE_KEY_PREFIX + sectionId;
    localStorage.setItem(key, String(Date.now()));
  } catch {}
}

function showOriginalPopup(sectionEl) {
  const popup =
    sectionEl.closest('.newsletter-popup--original') ||
    document.querySelector('.newsletter-popup--original');
  if (!popup) return;

  const sectionId = popup.dataset.sectionId;
  const delayDays = getDelayDays(popup);
  const delayMs = getDelaySeconds(popup);

  popup.querySelectorAll('.js-modal-close').forEach((btn) => {
    btn.addEventListener('click', () => {
      popup.classList.remove('modal--is-active');
      popup.style.display = 'none';
      markPopupClosed(sectionId);
      showOriginalReminder();
    });
  });

  initOriginalReminder(popup);

  if (!shouldShowPopup(sectionId, delayDays)) {
    showOriginalReminder();
    return;
  }

  setTimeout(() => {
    popup.style.display = '';
    popup.classList.add('modal--is-active');
  }, delayMs);
}

function initOriginalReminder(popup) {
  const wrapper = document.querySelector('.newsletter-reminder--original');
  if (!wrapper) return;

  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.addEventListener('click', () => {
      wrapper.style.display = 'none';
      if (popup) {
        popup.style.display = '';
        popup.classList.add('modal--is-active');
      }
    });
  }
}

function showOriginalReminder() {
  const wrapper = document.querySelector('.newsletter-reminder--original');
  if (!wrapper) return;
  wrapper.style.display = '';
  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.dataset.enabled = 'true';
  }
}

function showV2Popup(hideGiftBox = false) {
  const popup = document.querySelector('.newsletter-popup--v2');
  if (!popup) return;

  const sectionId = popup.dataset.sectionId;
  const delayDays = getDelayDays(popup);
  const delayMs = getDelaySeconds(popup);

  const closePopup = () => {
    popup.style.display = 'none';
    markPopupClosed(sectionId);
    if (!hideGiftBox) {
      showV2Reminder();
    }
  };

  popup.addEventListener('click', (e) => {
    if (
      e.target.closest('.newsletter-popup-v2__close') ||
      e.target.closest('.newsletter-popup-v2__overlay')
    ) {
      closePopup();
    }
  });

  if (!hideGiftBox) {
    initV2Reminder(popup);
  }

  if (popup.querySelector('.newsletter-v2-form-success')) {
    markPopupClosed(sectionId);
    if (window.toast) window.toast('🎁 Thanks for subscribing！');
    return;
  }

  if (popup.querySelector('.newsletter-v2-form-error')) {
    if (window.toast) window.toast('Login failed. Please try again.');
    return;
  }

  if (!shouldShowPopup(sectionId, delayDays)) {
    if (!hideGiftBox) {
      showV2Reminder();
    }
    return;
  }

  // 命中 hide_gift_box 实验组时立即展示，否则按配置延时展示
  if (hideGiftBox) {
    popup.style.display = 'block';
  } else {
    setTimeout(() => {
      popup.style.display = 'block';
    }, delayMs);
  }
}

function initV2Reminder(popup) {
  const wrapper = document.querySelector('.newsletter-reminder--v2');
  if (!wrapper) return;

  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.addEventListener('click', () => {
      if (reminder._wasDragged) {
        reminder._wasDragged = false;
        return;
      }
      wrapper.style.display = 'none';
      if (popup) popup.style.display = 'block';
    });
  }

  const closeBtn = wrapper.querySelector('.newsletter-reminder-v2__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      wrapper.style.display = 'none';
    });
  }
}

function showV2Reminder() {
  const wrapper = document.querySelector('.newsletter-reminder--v2');
  if (!wrapper) return;
  wrapper.style.display = '';
  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.dataset.enabled = 'true';
  }
}

async function initPopupAbTest() {
  const membershipPopup = createMembershipPopupController();
  if (membershipPopup) window.jjbMembershipPopup = membershipPopup;
  const sectionEl = getPopupSection();
  const canCheckGenerationCount = () =>
    Boolean(
      membershipPopup &&
      membershipPopup.designMode !== true &&
      membershipPopup.canAutoOpen() &&
      window.jjbMembershipCheckoutPending !== true &&
      !isActivePaidMembership(getThemeMembershipState()),
    );
  const generationCountPromise = canCheckGenerationCount()
    ? hasRequiredGenerationCountForAutoOpen()
    : Promise.resolve(false);

  try {
    const membershipExperiment = await getExperiment(
      MEMBERSHIP_EXPERIMENT_NAME,
    );
    const enabled =
      window.theme?.isVip === true ||
      membershipExperiment.get('enable', false) === true;
    window.zlog?.recordExperimentGroup?.(
      'show_vip_banner:enable',
      enabled ? 12 : 11,
    );
    if (
      enabled &&
      canCheckGenerationCount() &&
      (await generationCountPromise) &&
      canCheckGenerationCount()
    ) {
      membershipPopup.open({ entry_page: 'launch' });
      return;
    }
  } catch {}

  if (
    membershipPopup?.hasOpened() ||
    window.jjbMembershipCheckoutPending === true
  )
    return;
  if (!sectionEl) return;

  const group = 'new';
  const hideGiftBox = true;
  // try {
  //   const experiment = await getExperiment(EXPERIMENT_NAME);
  //   group = experiment.get('mode', 'old');
  //   recordExperimentGroup(
  //     EXPERIMENT_NAME,
  //     'mode',
  //     group,
  //     PROMOTION_POPUP_GROUP_IDS,
  //     34,
  //   );

  //   // 读取 hide_gift_box 实验：Control=false，Test=true，异常默认 false
  //   try {
  //     const hideGiftBoxExp = await getExperiment(HIDE_GIFT_BOX_EXPERIMENT_NAME);
  //     hideGiftBox = hideGiftBoxExp.get('enable', false) === true;
  //     recordExperimentGroup(
  //       HIDE_GIFT_BOX_EXPERIMENT_NAME,
  //       'enable',
  //       hideGiftBox,
  //       HIDE_GIFT_BOX_GROUP_IDS,
  //       37,
  //     );
  //   } catch {
  //     hideGiftBox = false;
  //   }
  // } catch {
  //   group = 'old';
  //   hideGiftBox = false;
  // }

  logEvent('promotion_popup_exposure', null, {
    group,
    hide_gift_box: hideGiftBox,
  });

  // if (group === 'none') {
  //   return;
  // }

  if (group === 'new') {
    showV2Popup(hideGiftBox);
  } else {
    showOriginalPopup(sectionEl);
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  initPopupAbTest();
}
